import {db, Messages} from "../models/models.js";
import {
    commaSeparate,
    facilitiesData,
    getCurrentGame,
    getMonthsDifference,
    getRandomChoice,
    getRandomInt,
    myround,
    staffFiredRamblings,
    staffHiredRamblings,
    toTitleCase
} from "./helpers.js"
import {Message} from "../models/message.js";
import {getGameCurrentUserCompany} from "./games.js";
import {getCompanyGame, getCompanyMessages, getCompanyObjectives, getCompanyStaff} from "../models/company.js";
import {
    getAvailableStaffMembers, getCompanyMainRosterSuperstars, getCompanyMonth,
    getCompanyRosterByType,
    getHighestLevelStaffMemberByRole,
    getNumberOfBuyoutSignings,
    getRandomStaffMemberByRole
} from "./companies.js";
import {superstarAggravatedInjuryTexts, superstarWorkThroughInjuryTexts} from "../data/injuries.js";
import {checkIfCanUnlearnTrait, evaluateInjury} from "./superstars.js";
import {getTalentRequestName} from "./talentRequests.js";
import {getChampionshipDisplayName} from "../ui/ui-helpers.js";
import {
    greetings,
    mysteriousAdvisorRamblings,
    ownerRamblings,
    staffRamblings,
    superstarJoinedInBuyoutTexts,
    superstarJoinedRamblings,
    superstarLeftRamblings,
    superstarNotAvailableBuyoutData,
    superstarRamblings
} from "../data/messages.js";
import {getPresidentData} from "./annualTournament.js";
import {getPowerupImage} from "./powerups.js";
import {getFeudParticipants} from "../models/feud.js";
import {getRosterSuperstars} from "../models/roster.js";
import {checkToGoToNextTutorialState} from "./tutorial.js";

async function getMessages() {  // get_messages
    let data = {};
    const currentGame = await getCurrentGame();
    const selectedCompany = await getGameCurrentUserCompany(currentGame);
    const messages = await getCompanyMessages(selectedCompany);
    messages.sort((a, b) => b.year - a.year || b.month - a.month || b.$loki - a.$loki);
    data['messages'] = messages.map(x => getMessageData(x, currentGame.monthCount, currentGame.year));

    // Check tutorial action
    if (selectedCompany.storyName === 'tutorial') {
        data['nextTutorialState'] = await checkToGoToNextTutorialState(selectedCompany, ['view_messages']);
        db.saveDatabase();
    }

    return data;
}

async function setMessageRead({uid} = {}) {  // update_message_read
    let data = {};
    let message = await Messages.get(uid);
    if (message) {
        message.read = true;
    }
    db.saveDatabase();
    return data;
}

async function deleteMessage({uid} = {}) {  // delete_message
    let data = {};
    let message = await Messages.get(uid);
    if (message) {
        await Messages.remove(message);
        db.saveDatabase();
    }
    return data;
}

async function deleteAllMessages({read} = {}) {  // delete_messages
    let data = {};
    const currentGame = await getCurrentGame();
    const selectedCompany = await getGameCurrentUserCompany(currentGame);
    let messages = await getCompanyMessages(selectedCompany);
    if (read) {
        messages = messages.filter(x => x.read)
    }
    for (const message of messages) {
        await Messages.remove(message)
    }
    db.saveDatabase();
    return data;
}

function getMessageData(message, month, year) {  // get_message_data
    let data = {};
    data['subject'] = message.subject;
    data['content'] = message.content;
    data['fromName'] = message.fromName;
    data['read'] = message.read;
    data['tag'] = message.tag;
    data['month'] = message.month;
    data['year'] = message.year;
    data['uid'] = message.$loki;
    data['monthsPassed'] = getMonthsDifference(month, year, message.month, message.year);
    data['subject'] = message.subject;
    data['subject'] = message.subject;
    return data;
}

async function createMessage(subject, fromName, month, year, content = null, tag = null) {  // Previously create_message
    const newMessage = Message();
    newMessage.subject = subject;
    newMessage.fromName = fromName;
    newMessage.content = content;
    newMessage.tag = tag;
    newMessage.month = month;
    newMessage.year = year;
    await Messages.insert(newMessage);
    return newMessage;
}

async function createStaffHiredMessage(staffMember, month, year) {  // Previously create_hired_staff_message
    const rambling = getRandomChoice(staffHiredRamblings);
    const subject = rambling['subject'];
    const newMessageText = `<p>${rambling['content']}</p>`;
    return createMessage(subject, staffMember.name, month, year, newMessageText, 'staff');
}

async function createStaffFiredMessage(staffMember, month, year) {  // Previously create_fired_staff_message
    const rambling = getRandomChoice(staffFiredRamblings);
    const subject = rambling['subject'];
    const newMessageText = `<p>${rambling['content']}</p>`;
    return createMessage(subject, staffMember.name, month, year, newMessageText, 'staff');
}

function createInjuryReturnPowerupMessage(superstar, month, year, doctor = null) {  // create_injury_return_powerup_message
    const subject = 'Early return';
    const content = `<p>I'm not sure how it was possible, but ${superstar.name} is ready to return to the ring. It's amazing how soon ${superstar.pronoun} recovered.</p>`;
    const messageFrom = doctor ? doctor.name : 'Staff';
    const tag = 'staff';
    return createMessage(subject, messageFrom, month, year, content, tag);
}

async function createResignPowerupMessage(superstar, month, year) {
    const subject = 'Here to stay';
    const content = '<p>No more part-time wrestling for me. I\'m ready to wrestle every week and every month.</p>';
    const messageFrom = superstar.name;
    const tag = 'superstar';
    return createMessage(subject, messageFrom, month, year, content, tag);
}

async function createRetirementReturnPowerupMessage(superstar, month, year) {
    const subject = 'Return';
    const content = "<p>It's great to be back. I can't wait for my return match.</p>";
    const messageFrom = superstar.name;
    const tag = 'superstar';
    return createMessage(subject, messageFrom, month, year, content, tag);
}

async function createRetirementReturnPermanentPowerupMessage(superstar, month, year) {
    const subject = 'Full return';
    const content = "<p>I'm back, and I'm not going away after a couple appearances. I'm here to stay and wrestle.</p>";
    const messageFrom = superstar.name;
    const tag = 'superstar';
    return createMessage(subject, messageFrom, month, year, content, tag);
}

async function createChangeObjectivePowerupMessage(objective, company, month, year) {
    const subject = 'New goal';
    const content = '<p>I want you to put your focus on my new goal:</p><p class=\"goal-color\">' +
        objective.name.replace('*', '<i class="fa fa-star margin-left-3"></i>') + '</p>';
    const messageFrom = company.ownerName || 'Owner';
    const tag = 'owner';
    return createMessage(subject, messageFrom, month, year, content, tag);
}

async function createOwnerAppearancePowerupMessage(company, month, year) {
    const subject = 'Booking';
    const content = "I'm gonna be stepping into the ring soon. You better book me well!";
    const messageFrom = company.ownerName || 'Owner';
    const tag = 'owner';
    return createMessage(subject, messageFrom, month, year, content, tag);
}

async function createStyleChangePowerupMessage(style, company, month, year) {
    const subject = 'Style Change';
    const content = `<p>From now on, we're going to be changing the style of our product to ${toTitleCase(style)}. I want you to work that style for all of our shows.</p>`;
    const messageFrom = company.ownerName || 'Owner';
    const tag = 'owner';
    return createMessage(subject, messageFrom, month, year, content, tag);
}

async function createRecruitmentPowerupMessage(topSuperstar, trainer, company, month, year) {
    const subject = 'New recruits';
    const content = `The Training Roster got some new recruits recently. ${topSuperstar.name} in particular looks to be a great prospect.</p>`;
    const messageFrom = trainer ? trainer.name : 'Staff';
    const tag = 'staff';
    return createMessage(subject, messageFrom, month, year, content, tag);
}

async function createStaffRecruitmentPowerupMessage(topStaffMember, talentManager, company, month, year) {
    const subject = 'New prospects';
    const content = `Some new professionals listed themselves for hire on the Staff Hiring Board. ${topStaffMember.name} in particular looks to be a great ${toTitleCase(topStaffMember.role.replace('_', ' '))}.</p>`;
    const messageFrom = talentManager ? talentManager.name : 'Staff';
    const tag = 'staff';
    return createMessage(subject, messageFrom, month, year, content, tag);
}

async function createPowerupMessage(powerup, month, year, unlock = true) {  // create_powerup_message
    let powerupsImage = getPowerupImage(powerup);
    let powerupHtml = '<p class="black">' + powerupsImage + '<span class="top--10px margin-left-5">' +
        powerup.name + '</span>' + '</p>';

    let subject;
    let text;
    if (powerup.storyName === 'mars') {
        if (unlock) {
            subject = 'Experiment discovered';
            text = 'A powerful experiment has been discovered from Zholov\'s notes.';
        }
        else {
            subject = 'Experiment re-use';
            text = 'I\'ve managed to create a re-use of one of Zholov\'s experiments.'
        }
    }
    else {
        if (unlock) {
            subject = 'Artifact discovered';
            text = 'A powerful wrestling artifact has been discovered.'
        }
        else {
            subject = 'Artifact re-use';
            text = 'I\'ve managed to create a re-use of one of our wrestling artifacts.'
        }
    }

    text = '<p>' + text + '</p>';
    text += powerupHtml;

    return createMessage(subject, 'Mysterious Advisor', month, year, text);
}

async function createPresidentFotcSuperstarMessage(superstar, game, month, year) {  // create_president_fotc_superstar_message
    const presidentData = getPresidentData(game);
    const subject = 'Icon';
    const content = `<p>${superstar.name} has become quite the icon for your company. It would be great to have ${superstar.objectivePronoun} participate in the next TGI; ${superstar.pronoun} could even be your winner!`;
    const messageFrom = presidentData['name'];
    return createMessage(subject, messageFrom, month, year, content);
}

async function createPresidentOverSuperstarMessage(superstar, game, month, year) {  // create_president_over_superstar_message
    const presidentData = getPresidentData(game);
    const newMessageText = `<p>${superstar.name} has gotten quite over with the fans, hasn't ${superstar.pronoun}? We would love to see ${superstar.objectivePronoun} compete at the next TGI.</p>`;
    return createMessage('Over', presidentData['name'], month, year, newMessageText);
}


async function createNoAppearancesMessage(superstar, month, year, talentManager) {  // create_no_appearances_message
    const subject = 'Out of Appearances';
    const content = `<p>${superstar.name} has run out of appearances for this company year.</p><p>You can wait for ${superstar.objectivePronoun} to re-sign next year, though I\'ve heard that some powerful Artifacts can lead to immediate re-signing.</p>`;
    const messageFrom = talentManager ? talentManager.name : 'Staff';
    return createMessage(subject, messageFrom, month, year, content, 'staff');
}

async function createLastAppearanceMessage(superstar, month, year, talentManager) {  // create_last_appearance_message
    const subject = 'Last Appearance';
    const content = `<p>${superstar.name} is on ${superstar.possessivePronoun} last appearances for this company year. Use ${superstar.objectivePronoun} wisely.</p>`;
    const messageFrom = talentManager ? talentManager.name : 'Staff';
    return createMessage(subject, messageFrom, month, year, content, 'staff');
}

async function createWorkThroughInjurySuperstarMessage(superstar, month, year) {  // create_work_through_injury_superstar_message
    const messageData = getRandomChoice(superstarWorkThroughInjuryTexts);
    const subject = messageData['subject'];
    const content = `<p>${messageData['content']}</p>`;
    return createMessage(subject, superstar.name, month, year, content, 'superstar');
}

async function createAggravatedInjurySuperstarMessage(superstar, month, year) {  // create_aggravated_injury_superstar_message
    const messageData = getRandomChoice(superstarAggravatedInjuryTexts);
    const subject = messageData['subject'];
    const content = `<p>${messageData['content']}</p>`;
    return createMessage(subject, superstar.name, month, year, content, 'superstar');
}

function createInjuryMessage(injuryTime, superstar, injuryType, workingThroughInjury, month, year, doctor = null) {  // create_injury_message
    let newMessageText;

    // Injury extended
    if (workingThroughInjury) {
        newMessageText = `<p>${superstar.name.trim()}'s injury has worsened and turned into a ${superstar.injuryName}. `;
        newMessageText += `The return timeline has been extended to ${injuryTime} months.</p>`;
        newMessageText += '<p>You can get away with working through an injury sometimes, ' +
            'but often it only aggravates the issue.</p>';
    }
    // New injury
    else {
        newMessageText = `<p>${superstar.name.trim()}'s injury was diagnosed as a ${superstar.injuryName}. `;
        if (injuryType === 'workable') {
            const monthsWord = Math.round(injuryTime) === 1 ? 'month' : 'months';
            newMessageText += `Fortunately, it's only a small issue that should clear up in about ${injuryTime} ${monthsWord}.` +
                '</p><p>It is possible to work through the injury, but I would not recommend it ' +
                'as it could make things worse.</p>';
        } else {
            newMessageText += `${toTitleCase(superstar.pronoun)} should be able to return in ${injuryTime} months.</p>`;
        }
    }

    // Check if rehab possible
    if (doctor && doctor.level >= 2 && superstar.injured >= 3) {
        newMessageText += `<p>You can assign me to rehab ${superstar.name} by selecting ${superstar.objectivePronoun}` +
            ` under <span class="prominent-grey">Manage > Roster > View</span> and clicking on ` +
            '<span class="prominent-grey">Rehab</span>.</p>';
    }

    const messageFrom = doctor ? doctor.name : 'Staff';
    return createMessage('Injury', messageFrom, month, year, newMessageText, 'staff');
}

async function createStaffLevelUpMessage(staffMember, month, year) {  // create_staff_level_up_message
    let newMessageText = '<p>After recent work, I noticed that my skills have increased.</p>';
    if (staffMember.role === 'doctor') {
        if (staffMember.level === 2) {
            newMessageText += '<p>I can now provide a shorter range on a Superstar\'s injury return timeline.</p>';
            newMessageText += '<p>Oh, and with the Rehab Facility (Level 2 Medical), I can offer rehab services ' +
                'to injured Superstars, bringing their returns closer.</p>';
            newMessageText += '<p>To assign me to rehab a talent, go to your Roster under ' +
                '<span class="prominent-grey">Manage > Roster > View</span>, select an injured talent ' +
                'and click on <span class="prominent-grey">Rehab</span>.</p>';
        }
        else if (staffMember.level === 3) {
            newMessageText += '<p>I can now provide a certain month for a Superstar\'s return.</p>';
            newMessageText += '<p>Oh, and with the Advanced (Level 3) Medical Facility, my rehab services for ' +
                'injured Superstars can have a higher chance to bring their returns closer.</p>';
        }
    }
    else if (staffMember.role === 'scout') {
        if (staffMember.level === 2) {
            newMessageText += '<p>I can now produce Researched Reports on indie talent, which includes Popularity, ' +
                'Contract Status, Workrate, Charisma, Morale, Fatigue and any Injuries. I also have ' +
                'Researched Info compiled for all available talent and Basic Info for all unavailable ' +
                'talent.</p>';
            newMessageText += '<p>I also have a higher chance to recruit talent and a decent chance to recruit ' +
                'them while scouting.</p>';
            newMessageText += '<p>Oh, and with the Interest-building Facility (Level 2 Scouting), I can get indie ' +
                'talent interested in joining our company, reducing their signing cost. My recruitment ' +
                'also has a slight chance to build interest.</p>'
        }
        else if (staffMember.level === 3) {
            newMessageText += '<p>I can now produce Deep Reports on indie talent, which includes Overness, Gimmick, ' +
                'Alignment, Traits and Face of the Company Status. I also have Deep Info ' +
                'compiled for all available talent and Researched Info for all unavailable talent, ' +
                'and I can view Talent Stats for competitors.</p>';
            newMessageText += '<p>I now have a higher chance to recruit talent and get them interested. I also ' +
                'have a good chance to recruit them while scouting and a decent chance to get them ' +
                'interested while recruiting.</p>';
            newMessageText += '<p>And with the Indie Tracking Facility (Level 3 Scouting), I can automatically ' +
                'recruit new talent that join the Indies!</p>'
        }
    }
    else if (staffMember.role === 'producer') {
        if (staffMember.level === 2) {
            newMessageText += '<p>My attempts to improve segments by focusing on them have a bigger effect and a ' +
                'higher chance to succeed.</p>';
            newMessageText += '<p>I can now create the following packages:</p>';
            newMessageText += '<p>- Return Package</p>';
            newMessageText += '<p>- Debut Package</p>';
            newMessageText += '<p>- Champion Package</p>';
            newMessageText += '<p>My packages will also be better in quality and I will boost new ' +
                'gimmicks upon debut.</p>';
            newMessageText += '<p>I can also add the following match finishes:</p>';
            newMessageText += '<p>- Switch Association</p>';
            newMessageText += '<p>- Throw In Towel</p>';
            newMessageText += '<p>- Respectful End</p>';
            newMessageText += '<p>- By Any Means</p>';
            newMessageText += '<p>Oh, and with the Championship Gimmicks Facility (Level 2 Production), you can ' +
                'add gimmicks to custom championships!</p>'
        }
        else if (staffMember.level === 3) {
            newMessageText += '<p>My attempts to improve segments by focusing on them have an even higher chance to ' +
                'succeed.</p>';
            newMessageText += '<p>I can now create the following packages:</p>';
            newMessageText += '<p>- Career Package</p>';
            newMessageText += '<p>- Day in the Life</p>';
            newMessageText += '<p>My packages will also be better in quality and I will further boost new ' +
                'gimmicks upon debut.</p>';
            newMessageText += '<p>I can also add the following match finishes:</p>';
            newMessageText += '<p>- Screwjob</p>';
            newMessageText += '<p>- Passout</p>';
            newMessageText += '<p>Oh, and with the Focused Packages Facility (Level 3 Production), I can ' +
                'create custom production packages! Go to book a Segment and you\'ll see a Custom ' +
                'Package option where you can tell me what to produce.</p>'
        }
    }
    else if (staffMember.role === 'trainer') {
        if (staffMember.level === 2) {
            newMessageText += '<p>My training program will now help talent gain a larger amount of experience (28 XP) ' +
                'and they will gain more experience (+ 1.5 XP) during Matches and Segments as well.</p>';
            newMessageText += '<p>I can now prepare specific talent in the Training Roster to make them available for ' +
                'callups. I will need two months for this in order to wrap up their affairs.</p>'
        }
        else if (staffMember.level === 3) {
            newMessageText += '<p>My training program will now help talent gain an even larger amount of experience (40 XP) ' +
                'and they will gain more experience (+ 1.5 XP) during Matches and Segments as well.</p>';
            newMessageText += '<p>I can now prepare specific talent in the Training Roster to make them available for ' +
                'callups in one month instead of two.</p>';
            newMessageText += '<p>And with the Accelerated Training Facility (Level 3 Training), I can easily ' +
                'bring newly signed talent straight to the Main Roster.</p>'
        }
    }
    else if (staffMember.role === 'talent_manager') {
        if (staffMember.level === 2) {
            newMessageText += '<p>My attempts to reassure talent have a bigger effect and a higher chance to succeed. ' +
                'I also have a higher chance to keep unhappy talent from leaving.</p>';
            newMessageText += '<p>Oh, and with the Guaranteed Appearances Facility (Level 2 Talent Management), ' +
                'I can negotiate a minimum of 3 appearances with part-timers when they ' +
                're-sign at the end of the year.</p>'
        }
        else if (staffMember.level === 3) {
            newMessageText += '<p>My attempts to reassure talent have an even bigger effect and a higher chance to succeed. ' +
                'I also have an even higher chance to keep unhappy talent from leaving and I can ' +
                'negotiate a minimum of 5 appearances with part-timers when they ' +
                're-sign at the end of the year.</p>';
            newMessageText += '<p>Also, with the Company Acquisition Facility (Level 3 Talent Management), I can ' +
                'negotiate deals with a large number of talent from the company we are buying out.</p>'
        }
    }

    const messageFrom = staffMember.name;
    return createMessage('Skill Increase', messageFrom, month, year, newMessageText, 'staff');
}

async function createGimmickChangeBoostMessage(superstar, momentName, month, year, talentManager = null) {  // create_gimmick_change_boost_message
    const newMessageText = `<p>Due to the <span class="olive">${momentName}</span>, a Gimmick change boost is available ` +
        `for ${superstar.name}.</p><p>If you change ${superstar.possessivePronoun} Gimmick before ` +
        `${superstar.possessivePronoun} next appearance, a boost will be added to the new Gimmick and the change will be free.</p>`;
    const messageFrom = talentManager ? talentManager.name : 'Staff';
    return createMessage('Gimmick Change', messageFrom, month, year, newMessageText, 'staff');
}

async function createSpecialSegmentUnlockedMessage(segmentItem, month, year) {  // create_special_segment_unlocked_message
    const itemImage = '<img alt="Special Segments" src="static/www/static/special_segments_icon.png"/>';
    const itemHtml = `<p class="special-segment-color">${itemImage}<span class="top--12px margin-left-5">${segmentItem.name}</span></p>`;

    const messageSubject = 'Special Segment';
    let newMessageText = '<p>A special segment has been uncovered.</p>';
    newMessageText += itemHtml;

    return createMessage(messageSubject, 'Mysterious Advisor', month, year, newMessageText);
}

async function createSuperstarLevelUpMessage(trainer, superstar, skill, month, year) {  // create_superstar_level_up_message
    let skillHtml;
    if (skill === 'workrate') {
        skillHtml = `<span class="workrate-blue">${toTitleCase(skill)}</span>`;
    } else if (skill === 'charisma') {
        skillHtml = `<span class="charisma-purple">${toTitleCase(skill)}</span>`;
    }
    const newMessageText = `<p>After recent performances, I noticed that ${superstar.name}'s ${skillHtml} skill has increased.</p>`;
    const messageFrom = trainer ? trainer.name : 'Staff';
    return createMessage('Skill Increase', messageFrom, month, year, newMessageText, 'staff');
}

async function createTraitAcquiredMessage(superstar, trait, company) {  // create_trait_acquired_message
    const game = await getCompanyGame(company);
    const canUnlearnTrait = checkIfCanUnlearnTrait(trait);

    const messageSubject = 'Trait Acquired';
    let newMessageText = `<p>After a recent check up on ${superstar.name}, I noticed that ${superstar.pronoun} has ` +
        `acquired the <span class="superstar-trait-color">${trait}</span> Trait.</p><p>Traits can be acquired when ` +
        `superstars perform certain actions.</p>`;
    if (canUnlearnTrait) {
        newMessageText += '<p>This Trait can be unlearned though, given considerable attention and time by a capable Staff Member.</p>';
    }

    const talentManager = getRandomStaffMemberByRole((await getCompanyStaff(company)), 'talent_manager');
    const messageFrom = talentManager ? talentManager.name : 'Staff';
    return createMessage(messageSubject, messageFrom, game.monthCount, game.year, newMessageText, 'staff');
}

async function createObjectiveCompletedMessage(objectiveName, reward, company) {  // create_objective_completed_message
    const game = await getCompanyGame(company);

    const objectiveHtml = `<p class="goal-color">${objectiveName.replace('*', '<i class="fa fa-star margin-left-3"></i>')}</p>`;
    let newMessageText = '<p>Good work on completing the following goal:</p>';
    newMessageText += objectiveHtml;
    newMessageText += `<p>I have rewarded you with <span class="blue margin-left-3 margin-right-3">` +
        `<img alt="Points" src="static/www/static/cp_icon.png" class="points-image-small four-px"/> ${reward}</span></p>`;

    const messageFrom = company.ownerName ? company.ownerName : 'Owner';
    return createMessage('Goal Reward', messageFrom, game.monthCount, game.year, newMessageText, 'owner');
}

async function createTalentRequestSucceededMessage(talentRequest, talent, moraleChange, month, year, talentManager,
                                                   xpGained) {  // create_talent_request_succeeded_message
    let verb = talent.objectName === 'superstar' ? 'is' : 'are';
    let moraleChangeHtml = `<span class="margin-left-2 margin-right-2 light-green">+ ${moraleChange}` +
                           `<span class="margin-left-2">%</span></span>`;
    let newMessageText = `<p>${talent.name} ${verb} happy that you completed ${talent.possessivePronoun} ` +
                         `"${await getTalentRequestName(talentRequest)}" request and ${talent.possessivePronoun} morale ${moraleChangeHtml} ` +
                         `has improved.</p>`;

    // Show xp gained
    if (xpGained) {
        let xpGainedHtml = `<span class="green margin-left-3 margin-right-3">+ ${xpGained} XP</span>`;
        newMessageText += `<p>I gained some experience ${xpGainedHtml} dealing with the request.</p>`;
    }

    let messageFrom = talentManager ? talentManager.name : 'Staff';
    return createMessage('Request completed', messageFrom, month, year, newMessageText, 'staff');
}

async function createTalentRequestFailedMessage(talentRequest, talent, moraleChange, month, year, talentManager) {  // create_talent_request_failed_message
    const moraleChangeHtml = `<span class="margin-left-2 margin-right-2 morale-dissatisfied-color">- ${moraleChange * -1}` +
        `<span class="margin-left-2">%</span></span>`;
    const newMessageText = `<p>${talent.name}'s "${await getTalentRequestName(talentRequest)}" request was not completed in time, ` +
        `and as a result, ${talent.possessivePronoun} morale ${moraleChangeHtml} has dropped.</p>`;
    const messageFrom = talentManager ? talentManager.name : 'Staff';
    return createMessage('Request failed', messageFrom, month, year, newMessageText, 'staff');
}

async function createFacilityUpgradeAvailableMessage(data, company) {  // create_facility_upgrade_available_message
    const game = await getCompanyGame(company);
    const levelPhrases = {2: 'a Developed', 3: 'an Advanced'};
    const levelPhrase = levelPhrases[data.level];
    const newMessageText = `<p>You have completed my requirements for the <span class="facility-color">${data['name']} ` +
        `Facility</span> upgrade. I will now allow you to change it to ${levelPhrase} <span class="gold">Level ${data['level']}</span>` +
        `Facility.</p>`;
    const messageFrom = company.ownerName || 'Owner';
    return createMessage('Upgrade Available', messageFrom, game.monthCount, game.year, newMessageText, 'owner');
}

async function createSuperstarMarkedOutMessage(superstar, segmentItemName, participant, month, year) {  // create_superstar_marked_out_message
  const subject = 'Marked Out';
  const newMessageText = `<p>Some people may not be happy with it, but that ${segmentItemName} segment with ` +
      `${participant.name} made me mark out!</p>`;
  return createMessage(subject, superstar.name, month, year, newMessageText, 'superstar');
}

async function createTalentReleasedMessage(talent, staffMembers, month, year, vacated = null, disbanded = null) {  // create_talent_released_message
    let newMessageText;
    if (talent.objectName === 'superstar') {
        newMessageText = `<p>${talent.name}'s contract has been terminated and ${talent.pronoun} has been released, as per your orders.</p>`;
    } else {
        newMessageText = `<p>${talent.name}'s contracts have been terminated and ${talent.pronoun} have been released, as per your orders.</p>`;
    }

    if (vacated) {
        for (let v of vacated) {
            let championshipName = getChampionshipDisplayName(v);
            if (v.tagTeam || v.trio) {
                newMessageText += `<p><span class="gold">${championshipName}</span> have been vacated.</p>`;
            } else {
                newMessageText += `<p><span class="gold">${championshipName}</span> has been vacated.</p>`;
            }
        }
    }
    if (disbanded) {
        for (let d of disbanded) {
            newMessageText += `<p>${d} have disbanded.</p>`;
        }
    }

    let talentManager = getRandomStaffMemberByRole(staffMembers, 'talent_manager');
    let messageFrom = talentManager ? talentManager.name : 'Staff';
    return createMessage(`${talent.name} released`, messageFrom, month, year, newMessageText, 'staff');
}

async function createAvailableSuperstarMessage(superstar, overnessIncrease, month, year, doctor = null) {  // create_available_superstar_message
    let newMessageText = `<p>${superstar.name} is ready to return to the ring.`;
    if (overnessIncrease) {
        newMessageText += ` ${toTitleCase(superstar.possessivePronoun)} overness should get a nice boost` +
            ` (<span class="hot-color">+${overnessIncrease}%</span>).</p>`;
    }
    let messageFrom = doctor ? doctor.name : 'Staff';
    return createMessage(`${superstar.name} available`, messageFrom, month, year, newMessageText, 'staff');
}

async function createInjuryReevaluatedMessage(injuryData, doctor, month, year) {  // create_injury_reevaluated_message
    const oldMonthWord = injuryData['oldTime'] === '1' ? 'month' : 'months';
    const newMonthWord = injuryData['time'] === '1' ? 'month' : 'months';

    let healingText = '';
    if (injuryData['rehabPerformed']) {
        healingText = ', sped up due to rehab';
    }

    const newMessageText = `<p>${injuryData['name']}'s return timeline from a ${injuryData['injuryName']} has been ` +
        `re-evaluated from <span class="grey">${injuryData['oldTime']} ${oldMonthWord}</span> to ` +
        `<span class="grey">${injuryData['time']} ${newMonthWord}</span>${healingText}.</p>`;
    const messageFrom = doctor?.name || 'Staff';

    return createMessage('Injury update', messageFrom, month, year, newMessageText, 'staff');
}

async function createInjuryRehabMessage(injuryData, doctor, month, year) {  // create_injury_rehab_message
    const oldMonthWord = injuryData['oldTime'] === '1' ? 'month' : 'months';
    const newMonthWord = injuryData['newTime'] === '1' ? 'month' : 'months';

    const newMessageText = `<p>${injuryData['name']}'s return timeline from a ${injuryData['injuryName']} has been ` +
        `sped up due to rehab, changing it from <span class="grey">${injuryData['oldTime']} ${oldMonthWord}</span> to ` +
        `<span class="grey">${injuryData['newTime']} ${newMonthWord}</span>.</p>`;
    const messageFrom = doctor?.name || 'Staff';

    return createMessage('Injury rehab', messageFrom, month, year, newMessageText, 'staff');
}

async function createWorkableInjuryRecoveryMessage(superstar, month, year, doctor = null) {  // create_workable_injury_recovery_message
    const newMessageText = `<p>${superstar.name}'s injury has healed completely and ${superstar.pronoun} can now ` +
        `wrestle without any added risks.</p>`;
    const messageFrom = doctor ? doctor.name : 'Staff';
    return createMessage(`${superstar.name} cleared`, messageFrom, month, year, newMessageText, 'staff');
}

async function createIndieSuperstarAvailableMessage(superstar, tagTeam, month, year, scout = null) {  // create_indie_superstar_available_message
    let tagTeamName = tagTeam ? ` (${tagTeam.name})` : '';
    const newMessageText = `<p>${superstar.name}${tagTeamName} has made ${superstar.objectivePronoun}self available to sign from the ` +
        `Indies. You should give ${superstar.objectivePronoun} a look.</p>`;
    const messageFrom = scout ? scout.name : 'Staff';
    return createMessage('Available Indie', messageFrom, month, year, newMessageText, 'staff');
}

async function createNewIndieSuperstarMessage(superstar, month, year, scout = null) {  // create_new_indie_superstar_message
    let newMessageText = `<p>A new talent, ${superstar.name}, has joined the Indies.</p>`;
    if (scout) {
        newMessageText += `<p>We can try scouting or recruiting ${superstar.objectivePronoun}.</p>`;
    }
    const messageFrom = scout?.name || 'Staff';
    return createMessage('Talent available', messageFrom, month, year, newMessageText, 'staff');
}

async function createNewIndieSuperstarRecruitedMessage(superstar, month, year, scout) {  // create_new_indie_superstar_recruited_message
    const reportLevels = {1: 'Basic', 2: 'Researched', 3: 'Deep'};
    const newMessageText = `<p>A new talent, ${superstar.name}, joined the Indies recently. With the help of the ` +
        `Indie Tracking Facility, I was able to recruit ${superstar.objectivePronoun}.</p><p>Take a look at the ` +
        `${reportLevels[scout.level] || 'Deep'} Info I compiled and see if you want to sign ${superstar.objectivePronoun}.</p>`;
    const messageFrom = scout?.name || 'Staff';
    return createMessage('New talent', messageFrom, month, year, newMessageText, 'staff');
}

async function createSpecialSegmentReuseMessage(segmentItem, month, year) {  // create_special_segment_reuse_message
    const itemImage = '<img alt="Special Segments" src="static/www/static/special_segments_icon.png"/>';
    const itemHtml = `<p class="special-segment-color">${itemImage}<span class="top--12px margin-left-5">${segmentItem.name}</span></p>`;
    const messageSubject = 'Special Segment';
    let newMessageText = '<p>A special segment is available to re-use.</p>';
    newMessageText += itemHtml;
    return createMessage(messageSubject, 'Mysterious Advisor', month, year, newMessageText);
}

async function createFutureSuperstarMessage(superstar, month, year, staffMembers, company, rosterName = 'Training') {  // create_future_superstar_message
    let newMessageText;
    if (rosterName === 'Training') {
        newMessageText = `<p>We just finished the recruitment of ${superstar.name}, who looks to be a Future Superstar.` +
            ` ${toTitleCase(superstar.pronoun)} has signed up with our Training Roster.</p>`;
    } else if (rosterName === 'Indie') {
        newMessageText = `<p>A new talent named ${superstar.name} has arrived on the Indies. ` +
            `${toTitleCase(superstar.pronoun)} looks to be a Future Superstar.</p>`;
    } else {
        newMessageText = `<p>${rosterName} have completed the recruitment of a new talent, ${superstar.name}, who ` +
            `looks to be a Future Superstar.</p>`;
    }
    const scout = getRandomStaffMemberByRole(staffMembers, 'scout');
    const messageFrom = scout ? scout.name : 'Staff';
    return createMessage('Future Superstar', messageFrom, month, year, newMessageText, 'staff');
}

async function createChampionshipNotUsedPenaltyMessage(championship, month, year, talentManager = null) {  // create_championship_not_used_penalty_message
    let newMessageText = `<p>You haven't booked the <span class="gold">${getChampionshipDisplayName(championship)}</span> in 6 months.</p>` +
        `<p>If you don't put it on the line this month, fans will start to lose interest and the ` +
        `Title's Prestige will be negatively affected.</p>`;
    let messageFrom = talentManager ? talentManager.name : 'Staff';
    return createMessage('Unused Championship', messageFrom, month, year, newMessageText, 'staff');
}

async function createStaffMemberRetirementMessage(staffMember, month, year) {  // create_staff_member_retirement_message
    const newMessageText = `<p>I have officially retired as a staff member, so you might want to find a suitable replacement.</p>` +
        `<p>I'm going to enjoy my time off now. Good luck.</p>`;
    const messageFrom = staffMember?.name || 'Staff';
    return createMessage('Retired', messageFrom, month, year, newMessageText, 'staff');
}

async function createStaffMemberRetirementChanceMessage(staffMember, month, year) { // create_staff_member_retirement_chance_message
    const newMessageText = `<p>Hey,</p><p>Just wanted to let you know that I'm thinking about retiring soon. I'm getting old, ` +
        `and a younger person could probably do a better job.</p><p>I'll be here this year, but I'm not sure about the coming ones.</p>`;
    const messageFrom = staffMember?.name || 'Staff';
    return createMessage('Retiring', messageFrom, month, year, newMessageText, 'staff');
}

async function createFeudEndedMessage(feudData, company, month, year) {  // create_feud_ended_message
    const participantNames = feudData['participants'].join(' and ');
    const newMessageText = `<p>I have ended the ${feudData['name']} Feud between ${participantNames}. Too much time ` +
        `has passed since the Feud started.</p>`;
    const messageFrom = company.ownerName || 'Owner';
    return createMessage('Feud Ended', messageFrom, month, year, newMessageText, 'owner');
}

async function createEndFeudReminderMessage(feudData, company, month, year) {  // create_end_feud_reminder_message
    const participantNames = feudData['participants'].join(' and ');
    const newMessageText = `<p>The ${feudData['name']} Feud between ${participantNames} has been going on for too long. ` +
        `I will automatically end it next month.</p>`;
    const messageFrom = company.ownerName || 'Owner';
    return createMessage('Long Feud', messageFrom, month, year, newMessageText, 'owner');
}

async function createTrainingRosterPopularityMessage(superstars, popularityChange, month, year, trainer) {  // create_training_roster_popularity_message
    const popularityChangeHtml = `<span class="green margin-left-2 margin-right-2">+ ${popularityChange}</span>`;
    const newMessageText = `<p>${commaSeparate(superstars)} gained a good amount of popularity ${popularityChangeHtml} while working ` +
        `the latest Training Roster Event.</p>`;
    const messageFrom = trainer?.name || 'Staff';
    return createMessage('Popularity gained', messageFrom, month, year, newMessageText, 'staff');
}

async function createTrainingRosterHotMessage(talent, month, year, trainer) {  // create_training_roster_hot_message
    const newMessageText = `<p>${talent.name} got Over with the fans at the latest Training Roster Event!</p>`;
    const messageFrom = trainer?.name || 'Staff';
    return createMessage('Hot superstar', messageFrom, month, year, newMessageText, 'staff');
}

async function createTrainingRosterStaleMessage(talent, month, year, trainer) {  // create_training_roster_stale_message
    const newMessageText = `<p>${talent.name} became Stale with the fans at the latest Training Roster Event ..</p>`;
    const messageFrom = trainer?.name || 'Staff';
    return createMessage('Stale superstar', messageFrom, month, year, newMessageText, 'staff');
}

async function createBuyoutAcceptedMessage(company, rosterSuperstars, available, notAvailable, month, year, talentManager) {  // create_buyout_accepted_message
    let newMessageText = `<p>${company.name} have accepted the Buyout Offer we made. The details should be ` +
        `finalized next month. Once they are, ${company.name} will shut down and we should absorb some of their fanbase.</p>`;
    newMessageText += `<p>Out of ${rosterSuperstars.length} Superstars on the Roster, negotiations went well with ` +
        `about ${myround(available.length, 5)}, including ${commaSeparate(available.slice(0, 3).map(x => x.name))}.</p>`;
    newMessageText += `<p>The rest of the Superstars have made it clear they are not willing to sign with us, and they ` +
        `include names like ${commaSeparate(notAvailable.slice(0, 3).map(x => x.name))}.</p>`;
    newMessageText += `<p>Most of the Superstars we don't sign will move to the Indies.</p>`;

    const messageFrom = talentManager?.name || 'Staff';
    return createMessage('Buyout accepted', messageFrom, month, year, newMessageText, 'staff');
}

async function createBuyoutAcceptedTrainerMessage(company, month, year, trainer) {  // create_buyout_accepted_trainer_message
    let newMessageText = `<p>Hey,</p>`;
    newMessageText += `<p>I hear you will decide which Superstars to sign from ${company.name}.</p>`;
    newMessageText += `<p>I wanted to let you know that I will send a small number of them to the Training Roster.</p>`;
    newMessageText += `<p>Looking forward to working with them.</p>`;
    const messageFrom = trainer?.name || 'Staff';
    return createMessage('Buyout signings', messageFrom, month, year, newMessageText, 'staff');
}

async function createBuyoutSigningsMessage(company, month, year, owner) {  // create_buyout_signings_message
    const numberOfBuyoutSignings = getNumberOfBuyoutSignings(company);
    const numberOfBuyoutChampionships = 3;

    let newMessageText = `<p>We're on our way to buying out ${company.name}! Many of the Superstars there want to join  ` +
        `us, but we can only afford a limited number.</p>`;
    newMessageText += `<p>Once the deal goes through, I will allow you to sign a maximum of ${numberOfBuyoutSignings} ` +
        `Superstars. Make sure you think hard about who you want to bring on board.</p>`;
    newMessageText += `<p>I will also allow you to bring a maximum of ${numberOfBuyoutChampionships} of their ` +
        `Championships into our Company.</p>`;

    const messageFrom = owner || 'Owner';
    return createMessage('Buyout assets', messageFrom, month, year, newMessageText, 'owner');
}

async function createBuyoutNotAvailableSuperstarMessage(superstar, month, year) {  // create_buyout_not_available_superstar_message
    const rambling = getRandomChoice(superstarNotAvailableBuyoutData);
    const subject = rambling['subject'];
    const newMessageText = `<p>${rambling['content']}</p>`;
    return createMessage(subject, superstar.name, month, year, newMessageText, 'superstar');
}

async function createCompetitorBuyoutAvailableMessage(competitor, month, year, talentManager) {  // create_competitor_buyout_available_message
    let newMessageText = `<p>This could be really exciting news. Due to falling in reputation recently, ` +
        `${competitor.name} is available for a buyout.</p>`;
    newMessageText += '<p>We can only make an offer if we have the Company Acquisition Facility (Level 3 Talent Management).</p>';
    newMessageText += '<p>To make an offer, go to <span class="prominent-grey">Manage > Job > Companies</span> and ' +
        'click the <span class="prominent-grey">Buyout</span> button.</p>';

    const messageFrom = talentManager?.name || 'Staff';
    return createMessage('Buyout', messageFrom, month, year, newMessageText, 'staff');
}

async function createCompetitorBuyoutNoLongerAvailableMessage(competitor, month, year, talentManager) { // create_competitor_buyout_no_longer_available_message
    const newMessageText = `<p>Due to new developments behind-the-scenes, ${competitor.name} is no longer available for a buyout.</p>`;
    const messageFrom = talentManager?.name || 'Staff';
    return createMessage('Buyout Off', messageFrom, month, year, newMessageText, 'staff');
}

async function createNewCompetitorMessage(competitor, owner, topSuperstars, month, year, talentManager) {  // create_new_competitor_message
    let newMessageText = `<p>A new wrestling Company, ${competitor}, has opened up, led by ${owner}.</p>`;
    newMessageText += `<p>A lot of talented Superstars have joined the Company, including ${commaSeparate(topSuperstars)}.</p>`;
    newMessageText += '<p>We should keep a close eye on how well they do.</p>';
    const messageFrom = talentManager?.name || 'Staff';
    return createMessage('New Competitor', messageFrom, month, year, newMessageText, 'staff');
}

async function createCompetitorSuperstarLeavingMessage(superstar, competitor, month, year, scout = null) {  // create_competitor_superstar_leaving_message
    let newMessageText = `<p>${superstar.name} is unhappy in ${competitor.name} and will be leaving when ` +
        `${superstar.possessivePronoun} contract expires.</p>`;
    newMessageText += `<p>If we make an offer now, ${competitor.name} might accept a lower amount than normal.</p>`;
    newMessageText += `<p>To make an offer, go to <span class="prominent-grey">Manage > Roster > View</span>, ` +
        `select the competitor in the dropdown, then select the talent and click on <span class="prominent-grey">Negotiate</span>.</p>`;
    const messageFrom = scout?.name || 'Staff';
    return createMessage('Potential Negotiation', messageFrom, month, year, newMessageText, 'staff');
}

async function createCompetitorSuperstarLeftMessage(superstar, company, month, year, scout = null) { // create_competitor_superstar_left_message
    const newMessageText = `<p>${superstar.name} has left ${company.name} due to ${superstar.possessivePronoun} ` +
        `contract ending. We can try to sign ${superstar.objectivePronoun} from the Indies.</p>`;
    const messageFrom = scout?.name || 'Staff';
    return createMessage('Available Superstar', messageFrom, month, year, newMessageText, 'staff');
}

async function createLeavingMessage(superstar, month, year, talentManager) {  // create_leaving_message
    const newMessageText = `<p>${superstar.name} has decided to leave the company when ${superstar.possessivePronoun} ` +
        `contract expires in ${superstar.contractMonthsRemaining} months. Due to ${superstar.possessivePronoun} ` +
        `unhappiness, ${superstar.pronoun} is not willing to stay on longer.</p><p>However, if you can make ` +
        `${superstar.name} happy before ${superstar.pronoun} leaves, we might be able to get ${superstar.objectivePronoun} to re-sign.</p>`;
    const messageFrom = talentManager?.name || 'Staff';
    return createMessage(`${superstar.name} leaving`, messageFrom, month, year, newMessageText, 'staff');
}

async function createNotLeavingMessage(superstar, month, year, talentManager) {  // create_not_leaving_message
    const newMessageText = `<p>Due to your efforts to make ${superstar.objectivePronoun} happy, we were able to ` +
        `convince ${superstar.name} to re-sign with the company.</p><p>${toTitleCase(superstar.pronoun)} is willing to stay with us ` +
        `for the foreseeable future.</p>`;
    const messageFrom = talentManager?.name || 'Staff';
    return createMessage(`${superstar.name} staying`, messageFrom, month, year, newMessageText, 'staff');
}

async function createLeftCompanyMessage(superstar, month, year, company, disbanded = [], vacated = []) {  // create_left_company_message
    let newMessageText = `<p>${superstar.name} refused our attempts to re-sign and ${superstar.possessivePronoun} ` +
        `contract ran out. ${toTitleCase(superstar.pronoun)} is no longer with the company.</p>`;
    for (const tagTeam of disbanded) {
        newMessageText += `<p>${tagTeam} have disbanded.</p>`;
    }
    for (const championship of vacated) {
        const championshipName = getChampionshipDisplayName(championship);
        if (championship.tagTeam || championship.trio) {
            newMessageText += `<p><span class="gold">${championshipName}</span> have been vacated.</p>`;
        } else {
            newMessageText += `<p><span class="gold">${championshipName}</span> has been vacated.</p>`;
        }
    }

    const talentManager = getRandomStaffMemberByRole((await getCompanyStaff(company)), 'talent_manager');
    const messageFrom = talentManager?.name || 'Staff';
    return createMessage(`${superstar.name} left`, messageFrom, month, year, newMessageText, 'staff');
}

async function createLeftCompanySuperstarMessage(superstar, month, year) {  // create_left_company_superstar_message
    const rambling = getRandomChoice(superstarLeftRamblings);
    const subject = rambling.subject;
    const newMessageText = `<p>${rambling.content}</p>`;
    return createMessage(subject, superstar.name, month, year, newMessageText, 'superstar');
}

async function createNoLongerUnhappyMessage(superstar, month, year, talentManager, xpGained = null) {  // create_no_longer_unhappy_message
    let newMessageText = `<p>After a talk with ${superstar.name}, ${superstar.pronoun} is no longer unhappy with ${superstar.possessivePronoun} position in the company.</p>`;
    newMessageText += `<p>${superstar.pronoun.charAt(0).toUpperCase() + superstar.pronoun.slice(1)} said ${superstar.pronoun} is willing to stay here and work with us.</p>`;

    if (xpGained) {
        const xpGainedHtml = `<span class="green margin-left-3 margin-right-3">+ ${xpGained} XP</span>`;
        newMessageText += `<p>I also gained some experience ${xpGainedHtml} after the talk.</p>`;
    }

    const messageFrom = talentManager?.name || 'Staff';
    return createMessage(`${superstar.name} not unhappy`, messageFrom, month, year, newMessageText, 'staff');
}

async function createUnhappyMessage(superstar, month, year, talentManager) {  // create_unhappy_message
    const newMessageText = `<p>${superstar.name} is unhappy with how we're using ${superstar.objectivePronoun}. ` +
        `If we don't do anything about it, ${superstar.pronoun} might decide to leave the company when ` +
        `${superstar.possessivePronoun} contract expires.</p>`;
    const messageFrom = talentManager?.name || 'Staff';
    return createMessage(`${superstar.name} unhappy`, messageFrom, month, year, newMessageText, 'staff');
}

async function generateRamblingMessage(superstars, company) {
    const pick = getRandomInt(0, 100);
    let messageFrom, tag, rambling;

    if (pick <= 85) {
        messageFrom = getRandomChoice(superstars).name;
        tag = 'superstar';
        rambling = getRandomChoice(superstarRamblings);
    } else if (pick <= 94) {
        messageFrom = company.ownerName ? company.ownerName : 'Owner';
        tag = 'owner';
        rambling = getRandomChoice(ownerRamblings);
    } else if (pick <= 98) {
        tag = 'staff';
        const availableStaffMembers = await getAvailableStaffMembers((await getCompanyStaff(company)));
        if (availableStaffMembers.length > 0) {
            messageFrom = getRandomChoice(availableStaffMembers).name;
        } else {
            messageFrom = 'Staff';
        }
        rambling = getRandomChoice(staffRamblings);
    } else {
        messageFrom = 'Mysterious Advisor';
        tag = '';
        rambling = getRandomChoice(mysteriousAdvisorRamblings);
    }

    const subject = rambling['subject'];
    const content = rambling['content'];

    return createUniqueMessage(content, subject, messageFrom, tag, company);
}

async function createUniqueMessage(content, subject, messageFrom, tag, company) {
    const game = await getCompanyGame(company);
    const gameMessagesSubjects = (await getCompanyMessages(company)).map(x => x.subject);
    if (!gameMessagesSubjects.includes(subject)) {
        return createMessage(subject, messageFrom, game.monthCount, game.year, `<p>${content}</p>`, tag);
    }
    return false;
}

async function createTalentOfferAcceptedMessage(talent, month, year, company) {  // create_talent_offer_accepted_message
    let verb = talent.objectName === 'superstar' ? 'is' : 'are';
    let newMessageText = `<p>We have accepted your offer for ${talent.name}. ${toTitleCase(talent.pronoun)} ${verb} ` +
        `ready to work for you immediately.</p>`;
    let messageFrom = company.ownerName ? company.ownerName : `Owner - ${company.name}`;
    return createMessage('Offer accepted', messageFrom, month, year, newMessageText);
}

async function createTalentOfferRejectedMessage(talent, month, year, company) {  // create_talent_offer_rejected_message
    let verb = talent.objectName === 'superstar' ? 'is' : 'are';
    let newMessageText = `<p>We have declined the offer you made for ${talent.name}. ${toTitleCase(talent.pronoun)} ` +
        `${verb} too valuable for us to let go of right now.</p>`;
    let messageFrom = company.ownerName ? company.ownerName : `Owner - ${company.name}`;
    return createMessage('Offer rejected', messageFrom, month, year, newMessageText);
}

async function createTalentOfferDeclinedMessage(talent, month, year, company) {  // create_talent_offer_declined_message
    let verb = talent.objectName === 'superstar' ? 'is' : 'are';
    let newMessageText = `<p>${talent.name} ${verb} not happy with how we handled the offer for ${talent.objectivePronoun}. ` +
        `The decision to not let ${talent.objectivePronoun} leave impacted ${talent.possessivePronoun} morale quite a bit.</p>`;
    let talentManager = getRandomStaffMemberByRole((await getCompanyStaff(company)), 'talent_manager');
    let messageFrom = talentManager ? talentManager.name : 'Staff';
    return createMessage(`${talent.name} not happy`, messageFrom, month, year, newMessageText, 'staff');
}

async function createTalentOfferRejectedByTalentMessage(talent, month, year, company) {
    let personalPronoun = talent.objectName === 'superstar' ? 'I' : 'we';
    let objectivePronoun = talent.objectName === 'superstar' ? 'me' : 'us';
    let newMessageText = `<p>${toTitleCase(personalPronoun)} have decided to sign with ${company.name} instead. They ` +
        `made a better offer and ${personalPronoun} believe they will be a good fit for ${objectivePronoun}.</p>`;
    let messageFrom = talent.name;
    return createMessage('Offer rejected', messageFrom, month, year, newMessageText, 'superstar');
}

async function createJoinedCompanySuperstarMessage(superstar, month, year) {  // create_joined_company_superstar_message
    const rambling = getRandomChoice(superstarJoinedRamblings);
    const subject = rambling['subject'];
    const newMessageText = `<p>${rambling['content']}</p>`;
    return createMessage(subject, superstar.name, month, year, newMessageText, 'superstar');
}

async function createTrainingFinishedMessage(talent, trainingData, month, year, trainer) {  // create_training_finished_message
    let xpWord = 'small';
    if (trainingData['xpGained'] >= 40) {
        xpWord = 'large';
    } else if (trainingData['xpGained'] >= 30) {
        xpWord = 'significant';
    } else if (trainingData['xpGained'] >= 20) {
        xpWord = 'decent';
    }

    let skillHtml = ''
    if (trainingData['skill'] === 'workrate') {
        skillHtml = `<span class="workrate-blue">${toTitleCase(trainingData['skill'])}</span>`;
    } else if (trainingData['skill'] === 'charisma') {
        skillHtml = `<span class="charisma-purple">${toTitleCase(trainingData['skill'])}</span>`;
    }

    let newMessageText = `<p>${talent.name}'s training is now complete. ${toTitleCase(talent.pronoun)} gained a ` +
        `${xpWord} amount of experience (${trainingData['xpGained']} XP) in ${talent.possessivePronoun} ${skillHtml} skill.</p>`;
    let messageFrom = trainer ? trainer.name : 'Staff';

    return createMessage('Training finished', messageFrom, month, year, newMessageText, 'staff');
}

async function createPreparingFinishedMessage(talent, month, year, trainer) {  // create_preparing_finished_message
    let newMessageText = `<p>${talent.name}'s preparation for being called up is now complete. ` +
        `${toTitleCase(talent.pronoun)} will be available at the next callup.</p>`;
    let messageFrom = trainer ? trainer.name : 'Staff';
    return createMessage('Preparation complete', messageFrom, month, year, newMessageText, 'staff');
}

async function createSuperstarInterestMessage(superstar, scout, month, year) {  // create_superstar_interest_message
    const newMessageText = `<p>Hey,</p><p>I had your scout, ${scout.name}, approach me recently. I wanted to personally ` +
        `tell you that I am currently interested in coming on board and working for your company.</p>`;
    return createMessage('Interested', superstar.name, month, year, newMessageText, 'superstar');
}

async function createSuperstarInterestedScoutMessage(superstar, scout, month, year) {  // create_superstar_interested_scout_message
    const newMessageText = `<p>I managed to get ${superstar.name}, who is available to sign in the Indies, interested ` +
        `in joining our company. You should be able to sign ${superstar.objectivePronoun} for a lower cost, though the ` +
        `interest will go away with time.</p>`;
    const messageFrom = scout ? scout.name : 'Staff';
    return createMessage('Interest developed', messageFrom, month, year, newMessageText, 'staff');
}

async function createRecruitmentSuccessMessage(talent, month, year, scout, interested = false) {  // create_recruitment_success_message
    const reportLevels = {1: 'Basic', 2: 'Researched', 3: 'Deep'};

    let newMessageText = `<p>I managed to successfully recruit ${talent.name} and ${talent.pronoun} ` +
        `${talent.pronoun === 'they' ? 'are' : 'is'} now available to sign in the Indies.</p>`;
    if (interested) {
        newMessageText += `<p>The recruitment went so well that ${talent.name} is interested in joining our company, ` +
            `which means you can sign ${talent.objectivePronoun} for a lower cost.</p>`;
    }
    newMessageText += `<p>Have a look at the ${reportLevels[scout.level] || 'Deep'} Info I compiled (in the Indies view) ` +
        `and see if you want to sign ${talent.objectivePronoun}.</p>`;

    const messageFrom = scout ? scout.name : 'Staff';
    return createMessage('Recruitment success', messageFrom, month, year, newMessageText, 'staff');
}

async function createRecruitmentFailMessage(talent, month, year, scout) {  // create_recruitment_fail_message
    const newMessageText = `<p>I was unsuccessful in recruiting ${talent.name}. We can try again this month though.</p>`;
    const messageFrom = scout ? scout.name : 'Staff';
    return createMessage('Recruitment failure', messageFrom, month, year, newMessageText, 'staff');
}

async function createScoutingReportMessage(data, scout, month, year, recruited = false) {  // create_scouting_report_message
    const reportAttributes = {
        1: ['rank', 'age', 'pronoun', 'unhappy', 'status', 'appearances'],
        2: ['popularity', 'charisma', 'workrate', 'injured', 'fatigue', 'morale'],
        3: ['overness', 'gimmick', 'alignment', 'faceOfTheCompany', 'cyborg', 'traits']
    };
    const reportLevels = {1: 'Basic', 2: 'Researched', 3: 'Deep'};
    const scoutLevel = scout ? scout.level : 1;

    let newMessageText = `<p>The ${reportLevels[scoutLevel] || 'Deep'} Report on ${data['name']} is complete. Here are the details: </p>`;

    for (let [level, attributes] of Object.entries(reportAttributes)) {

        // Get attributes for scouting level
        level = Number(level);
        if (scoutLevel >= level) {

            // Get attributes that are present in data
            for (let attribute of attributes.filter(x => Object.keys(data).includes(x))) {
                let value = data[attribute];

                // Add pronoun
                if (attribute === 'pronoun') {
                    // Don't show pronoun for teams
                    if (data['objectName'] === 'tagTeam') {
                        continue;
                    }
                    value = toTitleCase(String(value));
                }

                // Add stats
                if (['workrate', 'charisma'].includes(attribute)) {
                    if (typeof value === 'number' && !Number.isInteger(value)) {
                        value = `${value.toFixed(1)} / 3`;
                    } else {
                        value = `${value} / 3`;
                    }
                }

                // Add injured
                if (attribute === 'injured' && value) {
                    let injuredMonthWord = value === 1 ? 'month' : 'months';
                    value = `${value} ${injuredMonthWord}`;
                }

                // Add hot/stale
                if (attribute === 'overness') {
                    value = `${value}%`;
                    if (data['hot']) {
                        value += ' (hot)';
                    } else if (data['stale']) {
                        value += ' (stale)';
                    }
                }

                // Add alignment data
                if (attribute === 'alignment') {
                    if (!value) {
                        value = 'Neutral';
                    }
                    value = toTitleCase(String(value));
                    let alignmentMonths = data['alignmentMonths'];
                    if (alignmentMonths !== undefined) {
                        let alignmentMonthWord = alignmentMonths === 1 ? 'month' : 'months';
                        value += ` for ${alignmentMonths} ${alignmentMonthWord}`;
                    }
                }

                // Add gimmick data
                if (attribute === 'gimmick') {
                    if (!value) {
                        value = 'None';
                    } else {
                        value = value.name;
                    }
                }

                // Add traits data
                if (attribute === 'traits') {
                    if (!value.length) {
                        value = 'None';
                    } else if (typeof value[0] === 'object') {
                        value = value.map(y => `${y.name} (${y.superstar})`).join(', ');
                    } else {
                        value = commaSeparate(value);
                    }
                }

                // Add morale/fatigue
                if (['morale', 'fatigue'].includes(attribute)) {
                    value = `${value}%`;
                }

                if (value) {
                    if (value === true) {
                        value = 'Yes';
                    }
                    const name = attribute === 'faceOfTheCompany' ? 'Face of the Company' : toTitleCase(attribute);
                    if (typeof value !== 'string') {
                        value = String(value);
                    }
                    newMessageText += `<span class="margin-bottom-5 display-block">${name}: ${value}</span>`;
                }
            }
        }
    }

    if (!recruited) {
        newMessageText += `<p class="margin-top-10">If you want to pursue ${data['name']}, we can try recruiting ${data['objectivePronoun']}.</p>`;
    } else {
        newMessageText += `<p class="margin-top-10">The scouting went so well that I managed to recruit ${data['name']}. ` +
            `${toTitleCase(data['pronoun'])} ${data['pronoun'] === 'they' ? 'are' : 'is'} ` +
            `now available to sign in the Indies.</p>`;
    }

    const messageFrom = scout ? scout.name : 'Staff';
    return createMessage('Scouting report', messageFrom, month, year, newMessageText, 'staff');
}

async function createUnlearnTraitSuccessMessage(talent, month, year, traitName, staffMember) {  // create_unlearn_trait_success_message
    let newMessageText = `<p>After a lot of effort, I am pleased to say that the ` +
        `<span class="superstar-trait-color">${traitName}</span> Trait that ${talent.name} was associated with is no longer ` +
        `present in ${talent.objectivePronoun}.</p><p>${toTitleCase(talent.pronoun)} should be a better overall performer.</p>`;
    let messageFrom = staffMember ? staffMember.name : 'Staff';
    return createMessage('Unlearning success', messageFrom, month, year, newMessageText, 'staff');
}

async function createUnlearnTraitFailureMessage(talent, month, year, traitName, staffMember) {  // create_unlearn_trait_failure_message
    let newMessageText = `<p>Despite working quite a bit with ${talent.name}, I wasn't able to get ` +
        `${talent.objectivePronoun} to overcome the <span class="superstar-trait-color">${traitName}</span> Trait.</p><p>` +
        `Perhaps we can try again in the future?</p>`;
    let messageFrom = staffMember ? staffMember.name : 'Staff';
    return createMessage('Unlearning failure', messageFrom, month, year, newMessageText, 'staff');
}

async function createTransitionFinishedMessage(talent, month, year, trainer, role) {  // create_transition_finished_message
    let newMessageText = `<p>${talent.name}'s transition to a ${role} role is complete. ${toTitleCase(talent.pronoun)} ` +
        `is now available to use as a ${role}.</p>`;
    let messageFrom = trainer ? trainer.name : 'Staff';
    return createMessage('Transition complete', messageFrom, month, year, newMessageText, 'staff');
}

async function createTransitionToStaffFinishedMessage(superstar, month, year, staffMember, role) {  // create_transition_to_staff_finished_message
    let newMessageText = `<p>${superstar.name}'s transition to a ${toTitleCase(role.replace('_', ' '))} is now complete ` +
        `and ${superstar.pronoun} is ready to work with us.</p>`;
    let messageFrom = staffMember ? staffMember.name : 'Staff';
    return createMessage('Transition finished', messageFrom, month, year, newMessageText, 'staff');
}

async function createTransitionToStaffFinishedSuperstarMessage(superstar, month, year, role) {  // create_transition_to_staff_finished_superstar_message
    let newMessageText = `<p>I'm excited for my new journey as a ${toTitleCase(role.replace('_', ' '))}. Let me know ` +
        `what I should work on first.</p>`;
    return createMessage('Work', superstar.name, month, year, newMessageText, 'superstar');
}

async function createTransitionToSuperstarFinishedMessage(talent, month, year, staffMember) {  // create_transition_to_superstar_finished_message
    let newMessageText = `<p>${talent.name}'s transition back to a superstar is complete and ${talent.pronoun} is ` +
        `now available to use.</p>`;
    let messageFrom = staffMember ? staffMember.name : 'Staff';
    return createMessage('Transition finished', messageFrom, month, year, newMessageText, 'staff');
}

async function createReassureSuccessMessage(talent, moraleChange, month, year, talentManager) {  // create_reassure_success_message
    let moraleChangeHtml = `<span class="margin-left-2 margin-right-2 light-green">+ ${moraleChange}` +
        `<span class="margin-left-2">%</span></span>`;
    let newMessageText = `<p>I had a good talk with ${talent.name} and was able to reassure ${talent.objectivePronoun}. ` +
        `${talent.name}'s morale ${moraleChangeHtml} should get a boost.</p>`;
    let messageFrom = talentManager ? talentManager.name : 'Staff';
    return createMessage('Reassure success', messageFrom, month, year, newMessageText, 'staff');
}

async function createReassureFailureMessage(talent, moraleChange, month, year, talentManager) {  // create_reassure_failure_message
    let moraleChangeHtml = `<span class="margin-left-2 margin-right-2 morale-dissatisfied-color">- ${moraleChange * -1}` +
        `<span class="margin-left-2">%</span></span>`;
    let newMessageText = `<p>My talk with ${talent.name} didn't go so well. I wasn't able to reassure ` +
        `${talent.objectivePronoun} and ${talent.possessivePronoun} morale ${moraleChangeHtml} suffered.</p>`;
    let messageFrom = talentManager ? talentManager.name : 'Staff';
    return createMessage('Reassure failure', messageFrom, month, year, newMessageText, 'staff');
}

async function createUpcomingYearlyDraftMessage(company) {  // create_upcoming_yearly_draft_message
    let game = await getCompanyGame(company);
    let newMessageText = '<p>The Yearly Draft is coming up next month. It\'s an opportunity to freshen things up with ' +
                         'new matches and feuds.</p>' +
                         '<p>Try to finish up any existing stories before moving superstars to a different ' +
                         'brand.</p>';

    let talentManager = getRandomStaffMemberByRole((await getCompanyStaff(company)), 'talent_manager');
    let messageFrom = talentManager ? talentManager.name : 'Staff';
    return createMessage('Yearly Draft', messageFrom, game.monthCount, game.year, newMessageText, 'staff');
}

async function createAnnualTournamentOwnerWinMessage(winner, pointsBonus, company, annualTournament, month, year) {  // create_owner_win_message
    let newMessageText = `<p>Seeing ${winner.name} raise the trophy at ${annualTournament.name} was a wonderful sight.</p>`;
    newMessageText += `<p>It's a big deal for our company to win the tournament, and it got us some new fans, so ` +
        `I've given you a bonus of <span class="blue margin-left-3 margin-right-3"><img alt="Points" src="static/www/static/cp_icon.png" ` +
        `class="points-image-small four-px"/> ${pointsBonus}</span>.</p>`;
    let messageFrom = company.ownerName || 'Owner';
    return createMessage('Victory', messageFrom, month, year, newMessageText, 'owner');
}

async function createAnnualTournamentWinnerMessage(winner, annualTournament, month, year) {  // create_winner_message
    let newMessageText = `<p>It's a great honor to win ${annualTournament.name}. I think I'm going to keep holding ` +
        `the trophy for a month or so.</p>`;
    return createMessage('Trophy', winner.name, month, year, newMessageText, 'superstar');
}

async function createAnnualTournamentIndieWinnerAvailableMessage(winner, month, year, scout = null) {
    let newMessageText = `<p>Due to ${winner.possessivePronoun} TGI win, ${winner.name} is a hot commodity and ` +
        `available to sign from the Indies. You should make ${winner.objectivePronoun} an offer.</p>`;
    let messageFrom = scout ? scout.name : 'Staff';
    return createMessage('TGI Winner', messageFrom, month, year, newMessageText, 'staff');
}

async function createAnnualTournamentNoCompetitorsMessage(presidentName, month, year) {  // create_no_competitors_message
    let newMessageText = `<p>There isn't any competition in the wrestling industry these days, which means ` +
        `fans don't have much interest in The Grand Invitational. Sadly, we can't run it without going into a loss.</p>` +
        `<p>I really hope a new company springs up by next year.</p>`;
    return createMessage('No Competition', presidentName, month, year, newMessageText);
}

async function createAnnualTournamentPresidentWinMessage(presidentName, month, year) {
    let newMessageText = "<p>That was a great win for your company! The fans will be eager to see you defend your trophy.</p>";
    return createMessage('Great Win', presidentName, month, year, newMessageText);
}

async function createAnnualTournamentPresidentLossMessage(presidentName, month, year) {
    let newMessageText = '<p>Thank you for your company\'s part in The Grand Invitational. I hope to see you again next ' +
        'time where I\'m sure you can win!</p>';
    return createMessage('Thank You', presidentName, month, year, newMessageText);
}

async function createAnnualTournamentPresidentNoParticipationMessage(presidentName, winner, month, year) {  // create_no_participation_message
    let newMessageText = `<p>We had a great show at The Grand Invitational, with ${winner.name} taking the trophy ` +
        `home.</p>You don't want to miss participating next time. It could be your company that wins instead.</p>`;
    return createMessage('Great Show', presidentName, month, year, newMessageText);
}

async function createIntroducePowerupsMessage(powerup, company) {  // create_introduce_powerups_message
    let game = await getCompanyGame(company);
    let powerupImage = getPowerupImage(powerup);
    let powerupHtml = `<p class="black">${powerupImage}<span class="top--10px margin-left-5">${powerup.name}</span></p>`;

    let messageFrom, messageSubject, newMessageText;
    if (company.storyName === 'mars') {
        messageFrom = 'Company Lab';
        messageSubject = 'Experiment produced';
        newMessageText = `<p>We finished work on a new experiment. It's ready for you to use.</p>${powerupHtml}`;
    } else {
        messageFrom = 'Mysterious Advisor';
        messageSubject = 'Artifact discovered';
        newMessageText = `<p>A powerful wrestling artifact has been discovered in a dungeon below our company headquarters.</p>${powerupHtml}`;
        let holyShitHelpText = 'Gold Prestige Gimmick Matches';
        if (company.style === 'pure') {
            holyShitHelpText = 'High-rated Matches';
        }

        newMessageText += `<p>Use it carefully. More artifacts are known to be hidden in the wrestling world.</p>` +
            `<p>I believe offerings like creating Holy Shit Moments, winning Wrestling Awards and completing Owner Goals ` +
            `are sure to uncover more.</p>` +
            `<p>Holy Shit Moments can be created through ${holyShitHelpText} and Shocking Turns (Main Eventer turns ` +
            `in a Segment after a year or more of the same Alignment).</p>` +
            `<p>After you collect artifacts, you can activate them under <span class="prominent-grey">Manage > Other > ` +
            `Artifacts</span>, where I can also help you re-use them for some Creative Points.</p>`;
    }

    return createMessage(messageSubject, messageFrom, game.monthCount, game.year, newMessageText);
}

async function createIntroduceSpecialSegmentsMessage(specialSegment, company, numberOfUserUnlockedSegments = 0) {  // create_introduce_special_segments_message
    let game = await getCompanyGame(company);
    let specialSegmentHtml = `<p class="special-segment-color"><img alt="Special Segment" src="static/www/static/special_segments_icon.png"/>` +
        `<span class="top--12px margin-left-5">${specialSegment.name}</span></p>`;
    let messageFrom = 'Mysterious Advisor';
    let messageSubject = 'Special Segments';

    let newMessageText = `<p>A special segment has been discovered in the dusty vaults of our company library.</p>${specialSegmentHtml}`;

    // Add user segments to message
    if (numberOfUserUnlockedSegments > 0) {
        let segmentsWord = numberOfUserUnlockedSegments !== 1 ? 'segments' : 'segment';
        let segmentsVerb = numberOfUserUnlockedSegments !== 1 ? 'have' : 'has';
        newMessageText += `<p>${numberOfUserUnlockedSegments} other special ${segmentsWord} ${segmentsVerb} also ` +
            `been found from your previous encounters.</p>`;
    }

    newMessageText += `
        <p>Special Segments are unique items that always result in hot segments and get participants over. Use them ` +
        `carefully, for you can only use them once every 3 years and they have certain consequences. </p>` +
        `<p>A total of 10 Special Segments are known to exist in the wrestling world. You can go to ` +
        `<span class="prominent-grey">Manage > Other > Special</span> to learn more about them.</p>`;

    return createMessage(messageSubject, messageFrom, game.monthCount, game.year, newMessageText);
}

async function checkToAddCurrentObjectivesMessage(company) {  // create_objectives_message
    let incompleteObjectives = (await getCompanyObjectives(company)).filter(x => !x.completed && !x.past);
    let game = await getCompanyGame(company);
    let companyMessagesSubjects = (await getCompanyMessages(company)).map(x => x.subject);
    let messageFrom = company.ownerName ? company.ownerName : 'Owner';
    let newMessage = false;

    if (!company.storyName) {

        if (getCompanyMonth(company, game) === 0) {
            let messageSubject = 'Goals for this year';
            if (!companyMessagesSubjects.includes(messageSubject)) {
                // Add objectives message
                let objectivesMessageText = '<p>I\'m giving you the following goals for this year:</p>';
                for (let objective of incompleteObjectives) {
                    objectivesMessageText += `<p class="goal-color">` +
                        `${objective.name.replace('*', '<i class="fa fa-star margin-left-3"></i>')}</p>`;
                }
                if (!game.settings['unlimited_creative_points']) {
                    objectivesMessageText += '<p>Complete them and I\'ll award you a significant amount of Creative ' +
                        'Points. If you don\'t complete any of them, I\'ll have to take some Creative Points away. ' +
                        'You can view the goals under <span class="prominent-grey">Manage > Job > Goals</span>.</p>';
                } else {
                    objectivesMessageText += '<p>Complete them and I\'ll award you a significant amount of Creative ' +
                        'Points. If you don\'t complete any of them, I\'ll have to take some Creative Points away. ' +
                        'Not that it matters anymore.</p><p>You can view the goals under <span class="prominent-grey">' +
                        'Manage > Job > Goals</span>.</p>';
                }
                newMessage = await createMessage(messageSubject, messageFrom, game.monthCount, game.year,
                    objectivesMessageText, 'owner');
            }
        }

        if (getCompanyMonth(company, game) === 5) {
            let messageSubject = 'Goals update';
            if (!companyMessagesSubjects.includes(messageSubject)) {
                let objectivesMessageText = '';
                if (incompleteObjectives.length) {
                    objectivesMessageText = '<p>I wanted to remind you that I still have the following goals for the ' +
                        'current year: </p>';
                    for (let objective of incompleteObjectives) {
                        objectivesMessageText += `<p class="goal-color">` +
                            `${objective.name.replace('*', '<i class="fa fa-star margin-left-3"></i>')}</p>`;
                    }
                    objectivesMessageText += '<p>Complete all these goals and I\'ll give you a bonus reward at the end ' +
                        'of the year.</p>';
                } else {
                    objectivesMessageText = '<p>Great job on completing all the goals I gave you. You\'re on your way to ' +
                        'a hefty reward at the end of the year.</p>';
                }
                newMessage = await createMessage(messageSubject, messageFrom, game.monthCount, game.year,
                    objectivesMessageText, 'owner');
            }
        }

        if (getCompanyMonth(company, game) === 9) {
            if (incompleteObjectives.length === 3) {
                let messageSubject = 'Goals??';
                if (!companyMessagesSubjects.includes(messageSubject)) {
                    let objectivesMessageText = '<p>You haven\'t completed any of the following goals for this year: </p>';
                    for (let objective of incompleteObjectives) {
                        objectivesMessageText += `<p class="goal-color">` +
                            `${objective.name.replace('*', '<i class="fa fa-star margin-left-3"></i>')}</p>`;
                    }
                    if (!game.settings['unlimited_creative_points']) {
                        objectivesMessageText += '<p>If you don\'t complete at least one of these goals, I will take ' +
                            'some Creative Points away from you.</p>';
                    } else {
                        objectivesMessageText += '<p>If you don\'t complete at least one of these goals, I will take ' +
                            'some Creative Points away from you. But they don\'t matter anymore, so I will also scold you!</p>';
                    }
                    newMessage = await createMessage(messageSubject, messageFrom, game.monthCount, game.year,
                        objectivesMessageText, 'owner');
                }
            }
        }
    }

    return newMessage;
}

async function createObjectivesReviewMessage(data, company, game) {  // create_objectives_review_message
    let messageSubject = 'Goals Review';
    let newMessageText = '';
    let newMessage = null;
    if (data['bonus']) {
        newMessageText = '<p>I\'ve given you a bonus of ' +
                         '<span class="blue margin-left-3 margin-right-3"><img alt="Points" src="static/www/static/cp_icon.png" ' +
                         'class="points-image-small four-px"/> ' + data['bonus'] + '</span> for completing all the goals ' +
                         'for last year.</span>';
        if (data['newMatch']) {
            newMessageText += '<span class="display-block">' +
                              'As an additional reward, I\'ve created a special gimmick match (' +
                              '<span class="light-blue">' + data['newMatch'] + '</span>' +
                              ') unique to our company.</span>';
        }
        newMessageText += '<p>Complete this year\'s ' +
                          'goals and you\'ll get an even larger bonus.</p>';
    } else if (data['punishment']) {
        if (!game.settings['unlimited_creative_points']) {
            newMessageText = '<p>I\'ve taken away ' +
                             '<span class="blue margin-left-3 margin-right-3"><img alt="Points" src="static/www/static/cp_icon.png" ' +
                             'class="points-image-small four-px"/> ' + data['punishment'] +
                             '</span> for not completing any of my goals. I will take away even more Creative Points ' +
                             'if you do the same this year.</p>';
        } else {
            newMessageText = '<p>Bad booker! I\'ve taken away ' +
                             '<span class="blue margin-left-3 margin-right-3"><img alt="Points" src="static/www/static/cp_icon.png" ' +
                             'class="points-image-small four-px"/> ' + data['punishment'] +
                             '</span> for not completing any of my goals. ' +
                             'I will take away even more Creative Points if you do the same this year. Do as I say!</p>';
        }
    }
    if (newMessageText) {
        let messageFrom = company.ownerName ? company.ownerName : 'Owner';
        newMessage = await createMessage(messageSubject, messageFrom, game.monthCount, game.year, newMessageText, 'owner');
    }
    return newMessage;
}

function createPowerupsAwardedMessage(unlockedPowerups, awardedPowerups, company, game) {  // create_powerups_awarded_message
    let powerupHtml = '';
    for (let powerup of [...unlockedPowerups, ...awardedPowerups]) {
        let powerupImage = getPowerupImage(powerup, powerup.storyName);
        powerupHtml += '<p class="black">' + powerupImage + '<span class="top--10px margin-left-5">' +
                       powerup.name + '</span>' + '</p>';
    }

    let messageSubject;
    let newMessageText;
    if (unlockedPowerups.length) {
        if (unlockedPowerups.length > 1) {
            messageSubject = 'Artifacts discovered';
            newMessageText = 'Powerful wrestling artifacts have been discovered';
        } else {
            if (unlockedPowerups[0].storyName === 'mars') {
                messageSubject = 'Experiment discovered';
                newMessageText = 'A powerful experiment has been discovered from Zholov\'s notes';
            } else {
                messageSubject = 'Artifact discovered';
                newMessageText = 'A powerful wrestling artifact has been discovered';
            }
        }
    } else {
        if (awardedPowerups.length > 1) {
            messageSubject = 'Artifacts re-use';
            newMessageText = 'I\'ve managed to create a re-use of the following artifacts';
        } else {
            if (awardedPowerups[0].storyName === 'mars') {
                messageSubject = 'Experiment re-use';
                newMessageText = 'I\'ve managed to create a re-use of one of Zholov\'s experiments';
            } else {
                messageSubject = 'Artifact re-use';
                newMessageText = 'I\'ve managed to create a re-use of the following artifact';
            }
        }
    }

    newMessageText = '<p>' + newMessageText + '</p>';
    newMessageText += powerupHtml;

    return createMessage(messageSubject, 'Mysterious Advisor', game.monthCount, game.year, newMessageText);
}

async function createBuyoutCompleteMessage(company, month, year, talentManager, superstars, championships, teams, feuds,
                                     factions) {  // create_buyout_complete_message
    // Tell player about superstars, championships, teams, feuds, and factions transferred to their company
    let newMessageText = `<p>The ${company.name} Buyout is complete and the company has dissolved. ` +
        `We brought in the following <span class="superstar-blue">Superstars</span>:</p>`;
    for (const superstar of superstars) {
        newMessageText += `<p class="grey">- ${superstar.name}</p>`;
    }

    newMessageText += `<p>and the following <span class="championship-gold">Championships</span>:</p>`;
    for (const championship of championships) {
        newMessageText += `<p class="grey">- ${championship.name} Championship</p>`;
    }

    if (teams.length) {
        newMessageText += `<p>The following Teams were also added to the Roster:</p>`;
        for (const team of teams) {
            newMessageText += `<p class="grey">- ${team.name}</p>`;
        }
    }

    if (factions.length) {
        newMessageText += `<p>The following Factions were also added to the Roster:</p>`;
        for (const faction of factions) {
            newMessageText += `<p class="grey">- ${faction.name}</p>`;
        }
    }

    if (feuds.length) {
        newMessageText += `<p>And finally, these existing Feuds carried over:</p>`;
        for (const feud of feuds) {
            const feudParticipants = await getFeudParticipants(feud);
            newMessageText += `<p class="grey">- ${feud.name} (${feudParticipants.map((x) => x.name).join(' vs ')})</p>`;
        }
    }

    const messageFrom = talentManager ? talentManager.name : 'Staff';

    return createMessage('Buyout complete', messageFrom, month, year, newMessageText, 'staff');
}

async function createBuyoutCompleteTrainer(company, month, year, trainer, sentToTrainingRoster) {  // create_buyout_complete_trainer
    // Tell player about superstars sent to the training roster after buyout
    let newMessageText = `<p>I have sent the following ${company.name} Superstars to the <span class="green">Training Roster</span>:</p>`;
    for (const superstar of sentToTrainingRoster) {
        newMessageText += `<p class="grey">- ${superstar.name}</p>`;
    }

    const messageFrom = trainer ? trainer.name : 'Staff';

    return createMessage('Sent to training', messageFrom, month, year, newMessageText, 'staff');
}

async function createBuyoutCompleteDoctorMessage(company, month, year, doctor, injuredSuperstars) {  // create_buyout_complete_doctor_message
    // Tell player about superstars that are injured
    let newMessageText = `<p>The following ${company.name} Superstars have come into the Company injured:</p>`;
    for (const superstar of injuredSuperstars) {
        const injuryTime = evaluateInjury(superstar, doctor);
        const monthWord = injuryTime === '1' ? 'month' : 'months';
        if (superstar.injuryName) {
            newMessageText += `<p class="grey">- ${superstar.name}: ${toTitleCase(superstar.injuryName)} (${injuryTime} ${monthWord})</p>`;
        } else {
            newMessageText += `<p class="grey">- ${superstar.name} (${injuryTime} ${monthWord})</p>`;
        }
    }

    const messageFrom = doctor ? doctor.name : 'Staff';

    return createMessage('Injured talent', messageFrom, month, year, newMessageText, 'staff');
}

async function createBuyoutJoinedCompanyMessage(superstar, month, year) {
    const rambling = getRandomChoice(superstarJoinedInBuyoutTexts);
    const subject = rambling['subject'];
    const newMessageText = `<p>${rambling['content']}</p>`;
    return createMessage(subject, superstar.name, month, year, newMessageText, 'superstar');
}

async function createTalentRequestDeclinedMessage(talentRequest, talent, moraleChange, month, year, talentManager) {  // create_talent_request_declined_message
    let moraleChangeHtml = `<span class="margin-left-2 margin-right-2 morale-dissatisfied-color">- ${moraleChange * -1}
                            <span class="margin-left-2">%</span></span>`
    let newMessageText = `<p>${talent.name}'s "${await getTalentRequestName(talentRequest)}" request was declined, and ` +
        `as a result, ${talent.possessivePronoun} morale ${moraleChangeHtml} has dropped.</p>`
    let messageFrom = talentManager ? talentManager.name : 'Staff'
    return createMessage('Request declined', messageFrom, month, year, newMessageText, 'staff');
}

async function createIntroduceSandboxModeMessage(month, year, owner, settings) {  // create_introduce_sandbox_mode_message
    let newMessageText = '<p>Our company is doing quite well these days.</p>';

    if (settings['maximum_roster_capacity']) {
        newMessageText += '<p>Finances are terrific so we have a lot of room on the roster.</p>';
    }
    if (settings['all_indies_available']) {
        newMessageText += '<p>Everyone on the Indies is interested in signing with us.</p>';
    }
    if (settings['unlock_all_items']) {
        newMessageText += '<p>We have a wide variety of gimmick matches, feuds and segments to use. Plus, the ' +
                            'Mysterious Advisor keeps talking about a limitless supply of ancient artifacts.</p>';
    }
    if (settings['maximum_staff_and_facilities']) {
        newMessageText += '<p>Our staff is excellently trained and our company facilities are top of the line.</p>';
    }
    if (settings['no_special_items_cooldown']) {
        newMessageText += '<p>We\'re doing well creatively so you can use special segments and match finishes ' +
                            'as often as you want to.</p>';
    }
    if (settings['unlimited_creative_points']) {
        newMessageText += '<p>You don\'t have to worry about creative points anymore. Book whatever you want. ' +
                            'Go crazy.</p>';
    }
    if (settings['no_injuries']) {
        newMessageText += '<p>Injuries are non-existent. Our medical facility is so good that it will catch ' +
                            'any injuries before they happen!</p>';
    }
    if (settings['no_unhappiness']) {
        newMessageText += '<p>There\'s no need to worry about talent becoming unhappy. We pay them so much that ' +
                            'they won\'t ever complain.';
        if (settings['no_talent_requests']) {
            newMessageText += ' And they won\'t ever ask for anything!';
        }
        newMessageText += '</p>';
    }

    let messageFrom = owner ? owner : 'Owner';

    return createMessage('Good days', messageFrom, month, year, newMessageText, 'owner');
}

async function createIntroducePowerupsSandboxModeMessage(company, game) {  // create_introduce_powerups_sandbox_mode_message
    let messageFrom = 'Mysterious Advisor';
    let messageSubject = 'Hidden tomb';
    let newMessageText = '<p>Something amazing happened last night. I was walking around the dungeon below ' +
                        'our company headquarters when I discovered a hidden door with weird markings on it.</p>' +
                        '<p>I opened it to find a tomb filled with ancient artifacts. Not just a few, but hundreds ' +
                        'of them!</p>' +
                        '<p>The most surprising part is that when one of them is activated, it seems to ' +
                        'replenish itself.</p>' +
                        '<p>I don\'t know how this is possible. It must be some sort of enchantment from the past. ' +
                        'Whatever it is, be careful, and make good use of it.</p>' +
                        '<p>You can activate the artifacts under ' +
                        '<span class="prominent-grey">Manage > Other > Artifacts</span>.</p>';
    return createMessage(messageSubject, messageFrom, game.monthCount, game.year, newMessageText);
}

async function createCustomStartingCompanyCompetitorMessage(competitor, month, year) {  // create_custom_starting_company_competitor_message
    const subject = 'Good luck';
    const newMessageText = '<p>Hey, I heard about your new company and wanted to wish you good luck. ' +
                            'You\'re gonna need it.</p>';
    const messageFrom = competitor.ownerName ? competitor.ownerName : `Owner - ${competitor.name}`;
    return createMessage(subject, messageFrom, month, year, newMessageText);
}

async function createIntroduceFacilitiesMessage(month, year, owner, sandboxSettings = {}) {  // create_introduce_facilities_message
    const staffFacilities = Object.keys(facilitiesData);
    const facilitiesNames = commaSeparate(staffFacilities);
    let newMessageText = `<p>We have the following <span class="facility-color">Facilities</span> at our company: ${facilitiesNames}.</p>`;
    if (sandboxSettings['maximum_staff_and_facilities']) {
        newMessageText += '<p>Our Facilities are fully upgraded. You can view more information about them under ' +
                            '<span class="prominent-grey">Manage > Staff > Facilities</span>.</p>';
    } else {
        newMessageText += '<p>If you want to upgrade a Facility (JoW Paid), you\'ll have to complete ' +
                            'some requirements, which you can view under ' +
                            '<span class="prominent-grey">Manage > Staff > Facilities</span>.</p>' +
                            '<p>These upgrades will allow you to hire more and better skilled Staff as well as ' +
                            'bring other improvements to the company.</p>';
    }
    const messageFrom = owner ? owner : 'Owner';
    return createMessage('Company facilities', messageFrom, month, year, newMessageText, 'owner');
}

async function createIntroduceStaffMembersMessage(month, year, owner) {  // create_introduce_staff_members_message
    let newMessageText = `<p>We have space for the following <span class="staff-color">Staff Members</span> ` +
        `at our company: Doctor, Producer, Scout, Trainer and Talent Manager.</p>` +
        `<p>Each role can enhance our company in multiple ways. You can hire new Staff Members (JoW Paid) under ` +
        `<span class="prominent-grey">Manage > Staff > View > Hire</span>.</p>`;
    newMessageText += `<p>You can also transition retired Superstars to Staff Members by selecting them under ` +
        `<span class="prominent-grey">Manage > Roster > View</span> and clicking on ` +
        `<span class="prominent-grey">Transition To Staff</span>.</p><p>The number of Staff you can hire per role ` +
        `depends on the level of our Facilities.</p>`;
    const messageFrom = owner ? owner : 'Owner';
    return createMessage('Staff Members', messageFrom, month, year, newMessageText, 'owner');
}

async function createNewCompanyStaffMemberMessage(staffMember, company, game) {  // create_new_company_staff_member_message
    if (staffMember.role === 'doctor') {
        let mainRosterSuperstars = await getCompanyMainRosterSuperstars(company);
        let injuredSuperstars = mainRosterSuperstars.filter(x => x.injured);
        return createNewCompanyDoctorMessage(injuredSuperstars, game.monthCount, game.year, staffMember)
    }
    if (staffMember.role === 'scout') {
        return createNewCompanyScoutMessage(game.monthCount, game.year, staffMember)
    }
    if (staffMember.role === 'producer') {
        return createNewCompanyProducerMessage(game.monthCount, game.year, staffMember)
    }
    if (staffMember.role === 'trainer') {
        return createNewCompanyTrainerMessage(game.monthCount, game.year, staffMember)
    }
    if (staffMember.role === 'talent_manager') {
        return createNewCompanyTalentManagerMessage(game.monthCount, game.year, staffMember)
    }
}

async function createNewCompanyScoutMessage(month, year, scout, sandboxSettings = {}) {  // create_new_company_scout_message
    const greeting = getRandomChoice(greetings);
    let newMessageText = `<p>${greeting},</p><p>I\'m here to help you scout and recruit talent.</p>` +
        `<p>I recommend first scouting a talent to get a better idea of their status, then recruiting them to ` +
        `make them available to sign.</p>`;

    if (sandboxSettings['maximum_staff_and_facilities']) {
        newMessageText += `<p>I can produce Deep Reports on talent, which include everything you need to know about them.</p>
                           <p>To scout or recruit an indie talent, go to <span class="prominent-grey">Manage > Roster > ` +
            `Indies</span> and select an unavailable talent.</p>
                           <p>I will automatically compile Deep Info for talent that are available to sign in the Indies.</p>`;
    } else {
        newMessageText += `<p>Right now I can produce Basic Reports on talent, which include Rank, Age and Pronoun. My ` +
            `scouting also has a slight chance to recruit talent.</p>
                           <p>To scout or recruit an indie talent, go to <span class="prominent-grey">Manage > Roster > ` +
            `Indies</span> and select an unavailable talent.</p>
                           <p>I will automatically compile Basic Info for talent that are available to sign in the Indies.</p>`;
    }

    if (sandboxSettings['maximum_staff_and_facilities']) {
        newMessageText += `<p>My experience allows me to get indie talent interested in joining our company and ` +
            `automatically recruit new talent that join the Indies.</p>`;
    } else {
        newMessageText += `<p>As my skills increase, I can produce more detailed reports, view talent stats for ` +
            `competitors, get better at recruiting talent and get them interested in joining our company.</p>`;
    }

    const messageFrom = scout ? scout.name : 'Staff';
    return createMessage('Scouting', messageFrom, month, year, newMessageText, 'staff');
}

async function createNewCompanyTrainerMessage(month, year, trainer, sandboxSettings = {}) {  // create_new_company_trainer_message
    const greeting = getRandomChoice(greetings);
    let newMessageText = `<p>${greeting},</p><p>My job is to train talent and manage the Training Roster.</p>` +
        `<p>I can run training programs on talent that help them increase their skills.</p><p>To assign me to train a ` +
        `talent, go to your Roster under <span class="prominent-grey">Manage > Roster > View</span>, select a talent ` +
        `and click on <span class="prominent-grey">Train</span>.</p>`;
    if (sandboxSettings['maximum_staff_and_facilities']) {
        newMessageText += `<p>My skill level allows me to prepare specific talent in the Training Roster for callups ` +
            `and easily train new signings to bring them straight to the Main Roster.</p>`;
    } else {
        newMessageText += `<p>As my skills increase, I will be able to increase the experience that talent gain, choose ` +
            `who to make available for callups and easily train new signings to bring them straight to the Main Roster.</p>`;
    }
    newMessageText += '<p>New callups are available in Month 1 and Month 7.</p>';
    const messageFrom = trainer ? trainer.name : 'Staff';
    return createMessage('Training', messageFrom, month, year, newMessageText, 'staff');
}

async function createNewCompanyTalentManagerMessage(month, year, talentManager, sandboxSettings = {}) {  // create_new_company_talent_manager_message
    const greeting = getRandomChoice(greetings);
    let newMessageText = `<p>${greeting},</p><p>My job is to manage talent relations.</p><p>I can reassure talent, ` +
        `which increases their morale, and reduce the chance of them deciding to leave if unhappy.</p>` +
        `<p>To assign me to reassure a talent, go to your Roster under <span class="prominent-grey">Manage > Roster ` +
        `> View</span>, select a talent and click on <span class="prominent-grey">Reassure</span>.</p>` +
        `<p>Talent morale goes up by winning matches and titles, being in title matches and the main event, being ` +
        `part of hot matches/feuds/segments and feuding with top talent.</p><p>Morale goes down by losing matches ` +
        `and titles, feuding with lower talent and being in silly segments that flop.</p>`;

    if (sandboxSettings['maximum_staff_and_facilities']) {
        newMessageText += `<p>My experience allows me to negotiate for more appearances with part-timers and negotiate ` +
            `deals with more talent during Company Buyouts.</p>`;
    } else {
        newMessageText += `<p>As my skills increase, I will be able to reduce the chance of talent deciding to leave ` +
            `if unhappy, negotiate for more appearances with part-timers and negotiate deals with more talent during Company Buyouts (Level 3 Talent Management Facility).</p>`;
    }

    newMessageText += `<p>Competitors can become available for a buyout if their fan base drops low and their reputation ` +
        `decreases 5 or more levels than their highest reputation.</p>`;
    const messageFrom = talentManager ? talentManager.name : 'Staff';
    return createMessage('Talent management', messageFrom, month, year, newMessageText, 'staff');
}

async function createNewCompanyProducerMessage(month, year, producer, sandboxSettings = {}) {  // create_new_company_producer_message
    const greeting = getRandomChoice(greetings);
    let newMessageText = `<p>${greeting},</p>` +
        `<p>I'm here to help you boost talent using production.</p><p>I can create Production ` +
        `Packages that you can add to the show as a Segment.</p><p>These include Video Packages for Superstars ` +
        `and Tag Teams to boost their popularity, Feud Packages to boost Feud Hype and Event Packages to boost ` +
        `the Hype of the upcoming monthly event.</p><p>I can also focus on a certain Segment every show to try ` +
        `and improve its result.</p>`;

    if (sandboxSettings['maximum_staff_and_facilities']) {
        newMessageText += `<p>My skills allow me to boost new gimmick debuts and add new types of packages and match finishes.</p>`;
    } else {
        newMessageText += `<p>As my skills increase, I will be able to create better packages, boost new gimmick debuts ` +
            `and add new types of packages and match finishes.</p>`;
    }

    const messageFrom = producer ? producer.name : 'Staff';
    return createMessage('Production', messageFrom, month, year, newMessageText, 'staff');
}

async function createNewCompanyDoctorMessage(superstars, month, year, doctor, sandboxSettings = {}) {  // create_new_company_doctor_message
    const greeting = getRandomChoice(greetings);
    let newMessageText = `<p>${greeting},</p><p>I'm here to help talent deal with injuries.</p><p>If a talent gets ` +
        `injured, I will provide a diagnosis and start their recovery.</p>`;

    if (sandboxSettings['maximum_staff_and_facilities']) {
        newMessageText += `<p>My experience allows me to provide accurate diagnoses and offer rehab services to injured ` +
            `talent, bringing their returns closer.</p>
                           <p>To assign me to rehab a talent, go to your Roster under <span class="prominent-grey">` +
            `Manage > Roster > View</span>, select an injured talent and click on <span class="prominent-grey">Rehab</span>.</p>`;
    } else {
        newMessageText += `<p>As my skills increase, my diagnoses will get better and I will be able to offer rehab ` +
            `services to injured talent, bringing their returns closer.</p>`;
    }

    newMessageText += `<p>I would recommend keeping talent fatigue levels below 40% to reduce the chance of injuries.</p>`;

    if (superstars.length > 0) {
        newMessageText += `<p>The following superstars are on the injury shelf:</p>`;
        for (let s of superstars) {
            const injuryTime = evaluateInjury(s, doctor);
            const monthWord = injuryTime === '1' ? 'month' : 'months';
            newMessageText += `<p class="grey">- ${s.name} (${injuryTime} ${monthWord})</p>`;
        }
    }

    const messageFrom = doctor ? doctor.name : 'Staff';
    return createMessage('Medical', messageFrom, month, year, newMessageText, 'staff');
}

async function createInvitationAcceptedMessage(company, month, year) {  // create_invitation_accepted_message
    let newMessageText = `<p>I'm glad you decided to participate in The Grand Invitational. It'll be a big deal if we ` +
        `win, so make sure to send our top superstars.</p>`;
    let messageFrom = company['ownerName'] ? company['ownerName'] : 'Owner';
    return createMessage('Good Luck', messageFrom, month, year, newMessageText, 'owner');
}

async function createInvitationDeclinedMessage(company, month, year) {  // create_invitation_declined_message
    let newMessageText = `<p>We need to be represented at a tournament like The Grand Invitational. Consider doing so ` +
        `next time, and if we win, I'll throw in a reward for you.</p>`;
    let messageFrom = company['ownerName'] ? company['ownerName'] : 'Owner';
    return createMessage('Next Time', messageFrom, month, year, newMessageText, 'owner');
}

async function createPresidentChampionInjuryMessage(superstar, game, month, year) {  // create_president_champion_injury_message
    const presidentData = getPresidentData(game);
    const newMessageText = `<p>Even though The Grand Invitational is not liable for any injuries that happen during its ` +
        `events, I was sorry to see your champion get injured. I wish ${superstar.name} a speedy recovery.</p>`;
    return createMessage('Sorry', presidentData['name'], month, year, newMessageText);
}

async function createPartTimersMessage(partTimers, company, game, staffMembers) {  // create_part_timers_message
    let messageSubject = 'Contract changes';
    let newMessageText = '<p>The following Superstars have become part timers:</p>';
    for (const x of partTimers) {
        newMessageText += `<p class="grey">- ${x}</p>`;
    }

    let talentManager = getRandomStaffMemberByRole(staffMembers, 'talent_manager');
    let messageFrom = talentManager ? talentManager.name : 'Staff';
    return createMessage(messageSubject, messageFrom, game.monthCount, game.year, newMessageText, 'staff');
}

async function createTraitLostMessage(data, company, game, staffMembers) {  // create_trait_lost_message
    let messageSubject = 'Trait Lost';
    let newMessageText = `<p>I was doing a check up and noticed something different about ${data['superstar']}.</p>`;
    newMessageText += `<p>${toTitleCase(data['pronoun'])} no longer seems to have the ` +
        `<span class="superstar-trait-color">${data.trait}</span> Trait.</p>`;
    newMessageText += '<p>Traits can be lost as workers cross their early 40s.</p>';

    let talentManager = getRandomStaffMemberByRole(staffMembers, 'talent_manager');
    let messageFrom = talentManager ? talentManager.name : 'Staff';
    return createMessage(messageSubject, messageFrom, game.monthCount, game.year, newMessageText, 'staff');
}

async function createRetirementMessage(s, month, year, company, staffMembers) {  // create_retirement_message
    let messageSubject = `${s['name']} retires`;
    let newMessageText;

    if (s['hallOfFame']) {
        newMessageText = `<p>${s['name']} has retired and been inducted into the <span class="gold">Hall of Fame</span>. ` +
            `A moving ceremony was held:</p>`;
        for (let speech of s['hallOfFame']) {
            newMessageText += `<p>"${speech['content']}" - ${speech['superstar']}</p>`;
        }
    } else {
        newMessageText = `<p>${s['name']} has retired from wrestling.</p>`;
    }

    for (let tagTeam of s['tagTeams']) {
        newMessageText += `<p>${tagTeam} have disbanded.</p>`;
    }
    for (let championship of s['vacated']) {
        let championshipName = getChampionshipDisplayName(championship);
        if (championship['tagTeam'] || championship['trio']) {
            newMessageText += `<p><span class="gold">${championshipName}</span> have been vacated.</p>`;
        } else {
            newMessageText += `<p><span class="gold">${championshipName}</span> has been vacated.</p>`;
        }
    }

    newMessageText += `<p>Retired superstars can make the transition to Staff Members. Maybe that could be the next ` +
        `step in ${s['name']}'s career?</p>`;

    let talentManager = getRandomStaffMemberByRole(staffMembers, 'talent_manager');
    let messageFrom = talentManager ? talentManager.name : 'Staff';
    return createMessage(messageSubject, messageFrom, month, year, newMessageText, 'staff');
}

async function createContractsChangeMessage(partTimerChances, retirementChances, company, game, staffMembers) {  // create_contracts_change_message
    let messageSubject = 'Potential contract changes';
    let newMessageText = '';
    if (retirementChances.length) {
        newMessageText += '<p>The following Superstars are thinking about retiring:</p>';
        for (let x of retirementChances) {
            newMessageText += `<p class="grey">- ${x}</p>`;
        }
    }
    if (partTimerChances.length) {
        newMessageText += '<p>The following Superstars are thinking about becoming part timers:</p>';
        for (let x of partTimerChances) {
            newMessageText += `<p class="grey">- ${x}</p>`;
        }
    }

    let talentManager = getRandomStaffMemberByRole(staffMembers, 'talent_manager');
    let messageFrom = talentManager ? talentManager.name : 'Staff';
    return createMessage(messageSubject, messageFrom, game.monthCount, game.year, newMessageText, 'staff');
}

async function createPartTimersAppearancesSignedForMessage(company, game, superstars, talentManager, guaranteedAppearances,
                                                           minimumAppearances) {  // create_part_timers_appearances_signed_for_message
    let newMessageText;
    if (guaranteedAppearances) {
        newMessageText = '<p>The following part-timers have signed on for limited appearances this year.</p>';
        newMessageText += `<p>Due to the Guaranteed Appearances Facility, I was able to negotiate a minimum of ` +
            `${minimumAppearances} appearances for each Superstar.</p>`;
    } else {
        newMessageText = '<p>The following part-timers have signed on for limited appearances this year:</p>';
    }

    for (let s of superstars) {
        let monthWord = s.appearances === 1 ? 'appearance' : 'appearances';
        newMessageText += `<p class="grey">- ${s.name} (${s.appearances} ${monthWord})</p>`;
    }

    let messageFrom = talentManager ? talentManager.name : 'Staff';
    return createMessage('Part-timers', messageFrom, game.monthCount, game.year, newMessageText, 'staff');
}

async function checkForAvailableCallupsMessage(company, game) {  // check_for_available_callups_message
    let newMessage = null;
    let trainer = getHighestLevelStaffMemberByRole((await getCompanyStaff(company)), 'trainer');
    let trainerLevel = trainer ? trainer.level : 1;
    let monthsToCheck = trainerLevel < 3 ? [4, 10] : [5, 11];

    if (monthsToCheck.includes(getCompanyMonth(company, game))) {
        let trainingAvailableRoster = await getCompanyRosterByType(company, 'trainingAvailable');
        let availableNames = (await getRosterSuperstars(trainingAvailableRoster)).map(x => x.name);
        if (availableNames.length) {
            newMessage = await createAvailableForCallupMessage(availableNames, company, trainer);
        }
    }

    return newMessage;
}

function createAvailableForCallupMessage(availableNames, company, trainer) {  // create_available_for_callup_message
    let trainerLevel = trainer ? trainer.level : 1;
    let monthsPhrase = (trainerLevel < 3) ? 'in two months' : 'next month';

    let newMessageText = `<p>A Training Roster callup will be available ${monthsPhrase}. The following talent are ` +
        `available to callup:</p>`;
    for (const s of availableNames) {
        newMessageText += `<p class="grey">- ${s}</p>`;
    }

    if (trainerLevel < 2) {
        if (trainer) {
            newMessageText += '<p>As my skills increase, I will be able to prepare specific talent to make them available ' +
                'for callups.</p>';
        } else {
            newMessageText += '<p>With a skilled Trainer (JoW Paid), we will be able to prepare specific talent to make ' +
                'them available for callups.</p>';
        }
    } else {
        newMessageText += '<p>If you want to make a particular talent available for the next callup, you should task me ' +
            'to prepare them now.</p>';
    }

    let messageFrom = trainer ? trainer.name : 'Staff';
    return createUniqueMessage(newMessageText, 'Available Callups', messageFrom, 'staff', company);
}

async function createTopSuperstarPopularityMessage(topSuperstar, popularityBoost, company, month, year) {  // create_top_superstar_popularity_message
    let talentManager = getRandomStaffMemberByRole((await getCompanyStaff(company)), 'talent_manager');
    let popularityBoostHtml = '<span class="green margin-left-2 margin-right-2">+ ' + popularityBoost + '</span>';
    let newMessageText = '<p>' + topSuperstar.name + ' grabbed the top spot in this year\'s Top 50 Rankings! As a result, ' +
        topSuperstar.possessivePronoun + ' popularity ' + popularityBoostHtml + ' got a boost.</p>';
    let messageFrom = talentManager ? talentManager.name : 'Staff';
    return createMessage('Top Superstar', messageFrom, month, year, newMessageText, 'staff');
}

async function createTopSuperstarPointsMessage(topSuperstar, creativePointsBoost, company, month, year) {  // create_top_superstar_points_message
    let newMessageText = '<p>Good job getting ' + topSuperstar.name + ' to the #1 rank in this year\'s Top 50.</p>';
    newMessageText += '<p>I felt proud that one of our superstars topped the list, so I gave you a bonus of ' +
                      '<span class="blue margin-left-3 margin-right-3"><img alt="Points" src="static/www/static/cp_icon.png" ' +
                      'class="points-image-small four-px"/> ' + creativePointsBoost + '</span>.</p>';

    let messageFrom = company.ownerName ? company.ownerName : 'Owner';
    return createMessage('#1 Rank', messageFrom, month, year, newMessageText, 'owner');
}

async function createStaffMemberUnavailableMessage(staffMember, data, month, year) {  // create_staff_member_unavailable_message
    let text = '';
    let subject = '';

    if (data['name'] === 'illness') {
        text = '<p>I\'ve come down with a bad case of the flu. I don\'t think I will be available to work this ' +
            'month.</p>';
        subject = 'Flu';
    }
    else if (data['name'] === 'family-issue') {
        text = '<p>There\'s been a significant issue in my family .. I\'ll need to take some time off to work it ' +
            'out.</p>' +
            '<p>Hopefully, things will be alright soon. I think I should be back in about two months.</p>';
        subject = 'Family issue';
    }
    else if (data['name'] === 'court-case') {
        text = '<p>Recently, a fraudulent court case was registered against me, and until it\'s resolved, ' +
            'I\'m not allowed to work here.</p>' +
            '<p>I don\'t know how long it will take, but I will fight it and and I will be back.</p>';
        subject = 'Court case';
    }

    let newMessageText = `${text}`;
    let messageFrom = staffMember.name;
    return createMessage(subject, messageFrom, month, year, newMessageText, 'staff');
}

async function createFormingNewCompanyPotentialMessage(ownerName, month, year) {
    let newMessageText = `<p>Hey, I'm a big fan of your work. I thought I'd reach out to you about a new opportunity.</p>`;
    newMessageText += `<p>I've always had the dream to start my own wrestling company, and I think now's the right time to do it.</p>`;
    newMessageText += `<p>I really think we could make a viable alternative. Let me know if you're interested (<span class="prominent-grey">Manage > Job  > Companies > Form New Company</span>).</p>`;
    return createMessage('Opportunity', ownerName, month, year, newMessageText);
}

async function createFormingNewCompanyNewOwnerMessage(ownerName, month, year) {
    let newMessageText = '<p>I\'m really excited that you decided to take this chance with me!</p>';
    newMessageText += '<p>There\'s a lot to do, and I\'m going to get right to it. Meanwhile, think of what you\'d like to call the new company, and other details like names of our events and championships.</p>';
    newMessageText += '<p>I\'ll keep you posted.</p>';
    return createMessage('Excited', ownerName, month, year, newMessageText, 'owner');
}

async function createFormingNewCompanyCurrentOwnerMessage(ownerName, month, year) {
    let newMessageText = '<p>I heard the news that you\'re leaving us.</p>';
    newMessageText += '<p>I can\'t say I\'m surprised. You were running out of ideas, and a fresh direction would benefit us anyway.</p>';
    newMessageText += '<p>But to leave for a new company, that could fold at any time? I thought you were smarter than that.</p>';
    return createMessage('Leaving??', ownerName, month, year, newMessageText, 'owner');
}

async function createNewCompanyOwnerDetailsMessage(ownerName, newCompanyName, currentCompanyName, month, year) {
    let newMessageText = `<p>The details you sent look good, and the name "${newCompanyName}" is available! We should be ready to start in a couple of months.</p>`;
    newMessageText += `<p>Could you think of any talent at ${currentCompanyName} that would be interested in joining us? Perhaps talent that are happy with your work? We can make them an offer next month.</p>`;
    newMessageText += `<p>I'll also try to recruit suitable talent from the Indies and other companies.</p>`;
    return createMessage('Details', ownerName, month, year, newMessageText, 'owner');
}

async function createNewCompanyOwnerTalentMessage(ownerName, selectionsMade, month, year) {
    let newMessageText = '';
    if (selectionsMade) {
        newMessageText += `<p>The talent you selected are really interesting. I have sent offers to all of them, and we should have our roster ready soon.</p>`;
    }
    else {
        newMessageText += `<p>I'm surprised you didn't select any talent, but don't worry, I have my eye on some other superstars. We should have our roster ready soon.</p>`;
    }
    newMessageText += `<p>We officially start next month! You will be booking our first monthly event, and I can't wait to see it!</p>`;
    return createMessage('Talent', ownerName, month, year, newMessageText, 'owner');
}

async function createNewCompanyCurrentOwnerTalentMessage(ownerName, newCompanyName, month, year) {
    let newMessageText = `<p>It's one thing that you're leaving us, but some talent are thinking of leaving too??</p>`;
    newMessageText += `<p>No one is going to join this pointless company. I read somewhere that it's going to be called ${newCompanyName}, what a stupid name!</p>`;
    return createMessage('Pointless', ownerName, month, year, newMessageText, 'owner');
}

async function createNewCompanyInterestedTalentMessage(superstar, month, year) {
    let newMessageText = `<p>I heard that you'd like to bring me into the new company you're starting.</p>`;
    newMessageText += `<p>You should know that I'm on board. I'll follow you anywhere.</p>`;
    return createMessage('New Company', superstar.name, month, year, newMessageText, 'superstar');
}

async function createNewCompanyJoinedTalentMessage(ownerName, previousCompanyName, previousCompanySuperstars,
                                                   competitorsSuperstars, month, year) {
    let newMessageText = ``;
    if (previousCompanySuperstars.length) {
        newMessageText += `<p>We were able to sign the following talent from ${previousCompanyName}:</p>`;
        for (const superstar of previousCompanySuperstars) {
            newMessageText += `<p class="grey">- ${superstar.name}</p>`;
        }
    }
    if (competitorsSuperstars.length) {
        newMessageText += `<p>These superstars from other companies have signed with us:</p>`;
        for (const superstar of competitorsSuperstars) {
            newMessageText += `<p class="grey">- ${superstar.name}</p>`;
        }
    }
    newMessageText += `<p>We have also signed a bunch of talent from the Indies.</p>`;
    newMessageText += `<p>Let's book an amazing show!</p>`;
    return createMessage('Our Talent', ownerName, month, year, newMessageText, 'owner');
}

export {
    createStaffHiredMessage, createStaffFiredMessage, createMessage, createInjuryReturnPowerupMessage, createResignPowerupMessage,
    createRetirementReturnPowerupMessage, createRetirementReturnPermanentPowerupMessage, createChangeObjectivePowerupMessage,
    createOwnerAppearancePowerupMessage, getMessages, setMessageRead, deleteMessage, deleteAllMessages,
    createPowerupMessage, createPresidentFotcSuperstarMessage,createNoAppearancesMessage, createLastAppearanceMessage,
    createAggravatedInjurySuperstarMessage, createWorkThroughInjurySuperstarMessage, createInjuryMessage, createStaffLevelUpMessage,
    createGimmickChangeBoostMessage, createSpecialSegmentUnlockedMessage, createSuperstarLevelUpMessage,
    createPresidentOverSuperstarMessage, createTraitAcquiredMessage, createObjectiveCompletedMessage,
    createTalentRequestSucceededMessage, createTalentRequestFailedMessage, createFacilityUpgradeAvailableMessage,
    createSuperstarMarkedOutMessage, createTalentReleasedMessage, createBuyoutNotAvailableSuperstarMessage,
    createBuyoutAcceptedTrainerMessage, createCompetitorBuyoutNoLongerAvailableMessage, createChampionshipNotUsedPenaltyMessage,
    createCompetitorSuperstarLeavingMessage, createAvailableSuperstarMessage, createBuyoutAcceptedMessage,
    createBuyoutSigningsMessage, createCompetitorSuperstarLeftMessage, createEndFeudReminderMessage,
    createIndieSuperstarAvailableMessage, createCompetitorBuyoutAvailableMessage, createInjuryReevaluatedMessage,
    createFeudEndedMessage, createFutureSuperstarMessage, createInjuryRehabMessage, createLeavingMessage,
    createLeftCompanyMessage, createLeftCompanySuperstarMessage, createNewIndieSuperstarRecruitedMessage,
    createNewCompetitorMessage, createNewIndieSuperstarMessage, createNoLongerUnhappyMessage, createNotLeavingMessage,
    createSpecialSegmentReuseMessage, createStaffMemberRetirementChanceMessage, createStaffMemberRetirementMessage,
    createTrainingRosterHotMessage, createTrainingRosterPopularityMessage, createTrainingRosterStaleMessage,
    createUnhappyMessage, createWorkableInjuryRecoveryMessage, generateRamblingMessage, createTalentOfferRejectedMessage,
    createTalentOfferDeclinedMessage, createTalentOfferRejectedByTalentMessage, createTalentOfferAcceptedMessage,
    createJoinedCompanySuperstarMessage, createRecruitmentSuccessMessage, createPreparingFinishedMessage,
    createSuperstarInterestedScoutMessage, createRecruitmentFailMessage, createTransitionFinishedMessage,
    createSuperstarInterestMessage, createTransitionToStaffFinishedMessage, createScoutingReportMessage,
    createTrainingFinishedMessage, createUnlearnTraitFailureMessage, createTransitionToStaffFinishedSuperstarMessage,
    createUnlearnTraitSuccessMessage, createTransitionToSuperstarFinishedMessage, createReassureFailureMessage,
    createReassureSuccessMessage, createAnnualTournamentIndieWinnerAvailableMessage,
    createAnnualTournamentNoCompetitorsMessage, createUpcomingYearlyDraftMessage, createAnnualTournamentOwnerWinMessage,
    createAnnualTournamentWinnerMessage, createAnnualTournamentPresidentWinMessage,
    createAnnualTournamentPresidentNoParticipationMessage, createAnnualTournamentPresidentLossMessage,
    createIntroducePowerupsMessage, createIntroduceSpecialSegmentsMessage, checkToAddCurrentObjectivesMessage,
    createObjectivesReviewMessage, createPowerupsAwardedMessage, createBuyoutJoinedCompanyMessage,
    createBuyoutCompleteDoctorMessage, createBuyoutCompleteTrainer, createBuyoutCompleteMessage,
    createTalentRequestDeclinedMessage, createNewCompanyProducerMessage, createNewCompanyScoutMessage,
    createNewCompanyTalentManagerMessage, createNewCompanyTrainerMessage, createIntroduceFacilitiesMessage,
    createIntroduceSandboxModeMessage, createIntroducePowerupsSandboxModeMessage,
    createCustomStartingCompanyCompetitorMessage, createNewCompanyDoctorMessage, createIntroduceStaffMembersMessage,
    createInvitationAcceptedMessage, createInvitationDeclinedMessage, createPresidentChampionInjuryMessage,
    createPartTimersAppearancesSignedForMessage, createPartTimersMessage, createRetirementMessage,
    createContractsChangeMessage, createTraitLostMessage, checkForAvailableCallupsMessage, createTopSuperstarPointsMessage,
    createTopSuperstarPopularityMessage, createNewCompanyStaffMemberMessage, createStaffMemberUnavailableMessage,
    createStyleChangePowerupMessage, createRecruitmentPowerupMessage, createStaffRecruitmentPowerupMessage,
    createFormingNewCompanyNewOwnerMessage, createNewCompanyOwnerDetailsMessage, createNewCompanyOwnerTalentMessage,
    createFormingNewCompanyCurrentOwnerMessage, createNewCompanyCurrentOwnerTalentMessage,
    createNewCompanyInterestedTalentMessage, createNewCompanyJoinedTalentMessage, createFormingNewCompanyPotentialMessage}